import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { CreateProductFormData, ProductImage, PackageService, PackageDetails, ProductType } from '../../types/product';
import {
  CubeIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  TagIcon,
  ChevronRightIcon,
  PhotoIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import FormTabs from '../common/FormTabs';
import Modal from '../common/Modal';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCategories } from '../../hooks/useCategories';
import { useLanguage } from 'contexts/LanguageContext';
import { useStorage } from '../../hooks/useStorage';
import ImagePreview from '../common/ImagePreview';
import ImageModal from '../common/ImageModal';
import ConfirmImageDeleteModal from '../common/ConfirmImageDeleteModal';
import toast from 'react-hot-toast';
import { useProducts } from '../../hooks/useProducts';

// Kiterjesztett ProductImage típus a belső használatra, extra tulajdonságokkal
interface TempProductImage extends ProductImage {
  isTemp?: boolean;
  id?: string;
  file?: File;
}

interface ProductFormProps {
  onSubmit: (data: CreateProductFormData) => void;
  initialData?: Partial<CreateProductFormData> & { id?: string };
  onCancel: () => void;
  isOpen: boolean;
}

interface FormSection {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const FORM_SECTIONS: Record<string, FormSection> = {
  type: {
    title: 'Product Type',
    icon: CubeIcon,
    color: 'from-blue-500 to-primary-600'
  },
  details: {
    title: 'Product Details',
    icon: TagIcon,
    color: 'from-emerald-500 to-teal-600'
  },
  categories: {
    title: 'Categories',
    icon: TagIcon,
    color: 'from-orange-500 to-yellow-600'
  },
  pricing: {
    title: 'Pricing',
    icon: CurrencyDollarIcon,
    color: 'from-primary-500 to-pink-600'
  },
  images: {
    title: 'Product Images',
    icon: PhotoIcon,
    color: 'from-green-500 to-teal-600'
  },
  hiddenImages: {
    title: 'Hidden Images (Admin Only)',
    icon: PhotoIcon,
    color: 'from-red-500 to-orange-600'
  },
  package: {
    title: 'Package Services',
    icon: WrenchScrewdriverIcon,
    color: 'from-primary-500 to-primary-600'
  }
};

// Globális segéd változó a képek megőrzéséhez - komponens újrarenderelés esetén is megmarad
const lastUploadedImages = {
  images: [] as ProductImage[],
  hiddenImages: [] as ProductImage[], // Hidden images storage
  // Törölt képek URL-jeinek listája, ezeket ne hozzuk vissza később
  deletedImageUrls: new Set<string>(),
  deletedHiddenImageUrls: new Set<string>(), // Deleted hidden images URLs
  // Állapot változások nyomon követése, háttérlekérdezések kezelésére
  hasChanges: false,
  lastModified: 0,
  // Főkép URL-je
  mainImageUrl: '',
  // Eredeti képek száma inicializáláskor
  originalImageCount: 0,
  originalHiddenImageCount: 0
};

const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  initialData,
  onCancel,
  isOpen
}) => {
  const { organizations } = useOrganizations();
  const { mainCategories, categories, loading: categoriesLoading } = useCategories();
  // Fülek
  type FormTab = 'basic' | 'pricing' | 'images' | 'package';
  const [activeTab, setActiveTab] = useState<FormTab>('basic');
  const [tabErrors, setTabErrors] = useState<FormTab[]>([]);
  const tabCls = (tab: FormTab) => (activeTab === tab ? 'space-y-4' : 'hidden');
  const formRef = React.useRef<HTMLFormElement>(null);
  const onInvalid = (errs: Record<string, unknown>) => {
    const tabs = new Set<FormTab>();
    Object.keys(errs).forEach(name => {
      const el = formRef.current?.querySelector(`[name="${name}"], [name^="${name}."]`);
      const tab = el?.closest('[data-tab]')?.getAttribute('data-tab') as FormTab | null;
      if (tab) tabs.add(tab);
    });
    const list = Array.from(tabs);
    setTabErrors(list);
    if (list.length > 0 && !list.includes(activeTab)) setActiveTab(list[0]);
  };

  const { register, handleSubmit, watch, setValue, getValues, reset, formState: { errors } } = useForm<CreateProductFormData>({
    defaultValues: initialData || {
      type: 'product',
      organizations: [],
      isActive: true,
      isService: false
    }
  });
  const { t } = useLanguage();
  const { uploadImage, uploadState } = useStorage();
  const { products, generateProductCode, loading: productsLoading, checkCodeAvailability } = useProducts();

  // Kép törlési megerősítés state-ek
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDeleteIndex, setImageToDeleteIndex] = useState<number | null>(null);
  const [imageToDeleteUrl, setImageToDeleteUrl] = useState<string>('');

  // Kód ellenőrzés state-ek
  const [codeValidation, setCodeValidation] = useState<{
    isChecking: boolean;
    isValid: boolean;
    message: string;
  }>({
    isChecking: false,
    isValid: true,
    message: ''
  });

  // Kód ellenőrzés debounced logika
  const [codeCheckTimeout, setCodeCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Csomag szolgáltatások állapota
  const [packageServices, setPackageServices] = useState<PackageService[]>([]);
  const [packageDetails, setPackageDetails] = useState<Partial<PackageDetails>>({});
  
  // Kereshető dropdown state-ek
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  // Kód változás kezelése
  const handleCodeChange = async (code: string) => {
    // Töröljük a korábbi timeout-ot
    if (codeCheckTimeout) {
      clearTimeout(codeCheckTimeout);
    }

    // Ha üres a kód, reseteljük a validációt
    if (!code || code.trim() === '') {
      setCodeValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
      return;
    }

    // Ellenőrzés indítása state-ben
    setCodeValidation({
      isChecking: true,
      isValid: true,
      message: ''
    });

    // Debounced ellenőrzés (500ms késleltetéssel)
    const timeout = setTimeout(async () => {
      try {
        console.log('Kód ellenőrzés:', {
          code: code.trim(),
          excludeId: initialData?.id,
          hasInitialData: !!initialData,
          initialDataKeys: initialData ? Object.keys(initialData) : []
        });
        
        const result = await checkCodeAvailability(code.trim(), initialData?.id);
        
        if (result.isAvailable) {
          setCodeValidation({
            isChecking: false,
            isValid: true,
            message: t('products.validation.codeAvailable')
          });
        } else {
          setCodeValidation({
            isChecking: false,
            isValid: false,
            message: `${t('products.validation.codeInUse')}${result.existingProductName ? ` (${result.existingProductName})` : ''}`
          });
        }
      } catch (error) {
        console.error('Kód ellenőrzési hiba:', error);
        setCodeValidation({
          isChecking: false,
          isValid: true,
          message: ''
        });
      }
    }, 500);

    setCodeCheckTimeout(timeout);
  };

  // Reset form values when initialData changes or component opens
  useEffect(() => {
    if (isOpen) {
      // Reset form values with initialData
      const defaultValues = initialData ? {
        ...initialData,
        organizationId: initialData.organizationId || ''
      } : {
        type: 'product' as ProductType,
        organizations: [],
        isActive: true,
        isService: false,
        organizationId: ''
      };
      
      // Ha új termék (nincs initialData) és a termékek már be vannak töltve, generálunk kódot típus alapján
      if (!initialData && !productsLoading) {
        const productType = defaultValues.type || 'product';
        const generatedCode = generateProductCode(productType);
        const prefixName = productType === 'service' ? 'YSE' : productType === 'package' ? 'YPA' : 'YPR';
        console.log(`Automatikusan generált termékkód (${prefixName}):`, generatedCode);
      }
      
      reset(defaultValues);
      
      // Reset global image cache when component opens
      lastUploadedImages.images = initialData?.images || [];
      lastUploadedImages.hiddenImages = initialData?.hiddenImages || [];
      lastUploadedImages.deletedImageUrls = new Set<string>();
      lastUploadedImages.deletedHiddenImageUrls = new Set<string>();
      lastUploadedImages.hasChanges = false;
      lastUploadedImages.lastModified = 0;
      lastUploadedImages.mainImageUrl = '';
      lastUploadedImages.originalImageCount = initialData?.images?.length || 0;
      lastUploadedImages.originalHiddenImageCount = initialData?.hiddenImages?.length || 0;
      
      // Reset local state
      setIsUploading(false);
      setThumbnailUrl(initialData?.thumbnailUrl || '');
      setLocalImages(initialData?.images || []);
      setLocalHiddenImages(initialData?.hiddenImages || []);

      // Reset csomag adatok
      if (initialData?.packageDetails) {
        setPackageServices(initialData.packageDetails.services || []);
        setPackageDetails({
          estimatedDuration: initialData.packageDetails.estimatedDuration,
          totalHours: initialData.packageDetails.totalHours,
          methodology: initialData.packageDetails.methodology,
          deliverables: initialData.packageDetails.deliverables,
          requirements: initialData.packageDetails.requirements,
          timeline: initialData.packageDetails.timeline
        });
      } else {
        setPackageServices([]);
        setPackageDetails({});
      }

      // Reset kód validáció
      setCodeValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });

      // Reset last generated type
      setLastGeneratedType('');
    }
  }, [isOpen, initialData, reset, productsLoading]);

  // Kód mező figyelése
  const watchedCode = watch('code');
  useEffect(() => {
    if (watchedCode) {
      handleCodeChange(watchedCode);
    }
    
    // Cleanup timeout when component unmounts or code changes
    return () => {
      if (codeCheckTimeout) {
        clearTimeout(codeCheckTimeout);
      }
    };
  }, [watchedCode]);

  // Típus változás figyelése - automatikus kód újragenerálás
  const watchedType = watch('type');
  const [lastGeneratedType, setLastGeneratedType] = useState<string>('');
  
  useEffect(() => {
    // Csak új termék esetén (nincs initialData) és ha a termékek már be vannak töltve
    if (!initialData && !productsLoading && watchedType && watchedType !== lastGeneratedType) {
      const currentCode = getValues('code');
      // Ellenőrizzük, hogy az aktuális kód automatikusan generált-e (YSE/YPR/YPA prefix)
      const isAutoGeneratedCode = currentCode && (
        currentCode.startsWith('YSE-') || 
        currentCode.startsWith('YPR-') || 
        currentCode.startsWith('YPA-')
      );
      
      // Ha automatikusan generált kód van, újrageneráljuk a típus alapján
      if (isAutoGeneratedCode || !currentCode) {
        const newCode = generateProductCode(watchedType);
        setValue('code', newCode, { shouldValidate: false });
        setLastGeneratedType(watchedType);
        console.log(`Típus váltás: ${watchedType} -> új kód: ${newCode}`);
      }
    }
  }, [watchedType]);

  // Kép feltöltés kezelése
  const [isUploading, setIsUploading] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [localImages, setLocalImages] = useState<Array<TempProductImage>>([]);
  
  // Hidden images state management
  const [isUploadingHidden, setIsUploadingHidden] = useState(false);
  const [localHiddenImages, setLocalHiddenImages] = useState<Array<TempProductImage>>([]);
  
  // Figyeljük a képeket is
  const productImages = watch('images') || [];
  const productThumbnail = watch('thumbnailUrl') || '';
  
  // Ref a fájl inputhoz, hogy törölhessük az értékét feltöltés után
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Kereshető dropdown bezárása kattintásra kívülre
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setIsProductDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Parciális adatmentési segédfüggvény - csak a kép adatokat menti
  const saveImagesOnly = () => {
    // Csak akkor mentsünk, ha van initialData (azaz szerkesztési módban vagyunk)
    if (!initialData) {
      console.log('Nem mentünk képeket, mert új termék hozzáadása van folyamatban');
      return;
    }
    
    // Képadatok előkészítése
    const stableImages = localImages.filter(img => !img.isTemp).map(img => ({
      url: img.url || '',
      isMain: img.isMain || false,
      ...(img.title && { title: img.title }),
      ...(img.alt && { alt: img.alt }),
      ...(img.sortOrder && { sortOrder: img.sortOrder })
    }));
    
    // Biztosítsuk, hogy van főkép
    let mainImageExists = stableImages.some(img => img.isMain);
    if (!mainImageExists && stableImages.length > 0) {
      stableImages[0].isMain = true;
      mainImageExists = true;
    }
    
    // Thumbnail URL beállítása
    let thumbnailUrlValue = '';
    if (mainImageExists) {
      const mainImage = stableImages.find(img => img.isMain);
      if (mainImage) {
        thumbnailUrlValue = mainImage.url;
      }
    }
    
    console.log('Csak a képadatok mentése:', {
      képek: stableImages.length,
      főkép_index: stableImages.findIndex(img => img.isMain),
      thumbnailUrl: truncateUrl(thumbnailUrlValue, 30)
    });
    
    // Megőrizzük a termék jelenlegi adatait és csak a képeket frissítjük
    const currentFormData = getValues();
    
    // Frissítjük a képeket és a thumbnail URL-t
    currentFormData.images = stableImages;
    currentFormData.thumbnailUrl = thumbnailUrlValue;
    
    // Küldés az onSubmit callback-kel
    onSubmit(currentFormData);
    
    // Értesítés a felhasználónak
    notify('Képek sikeresen frissítve!');
  };
  
  // Extra watchEffect a képek szinkronban tartásához
  useEffect(() => {
    // Ha már vannak képek a form-ban, de még nincsenek a lokális állapotban
    if (productImages.length > 0 && localImages.length === 0) {
      console.log('Képek szinkronizálása form -> lokális állapot', productImages.length);
      setLocalImages(productImages);
    }
    
    // Ha eltérés van a form és a lokális állapot között, és nem ideiglenes feltöltés folyik
    if (!isUploading && productImages.length !== localImages.filter(img => !img.isTemp).length) {
      // Csak a stabil képek különbségét ellenőrizzük
      const formUrls = productImages.map(img => img.url);
      const localUrls = localImages.filter(img => !img.isTemp).map(img => img.url);
      
      // Ha eltérés van, keressük az eltűnt képeket
      const missingUrls = formUrls.filter(url => !localUrls.includes(url));
      const extraUrls = localUrls.filter(url => !formUrls.includes(url));
      
      if (missingUrls.length > 0 || extraUrls.length > 0) {
        // ELLENŐRIZZÜK: ne állítsunk vissza képeket, amiket szándékosan töröltünk
        const deletedUrls = Array.from(lastUploadedImages.deletedImageUrls);
        const isRestoringDeletedImages = missingUrls.some(url => 
          deletedUrls.some(deletedUrl => isSameImage(url, deletedUrl))
        );
        
        if (isRestoringDeletedImages) {
          console.log('Törölt képek visszaállítása megakadályozva!', {
            hiányzó_urlök: missingUrls.map(url => truncateUrl(url, 30)),
            törölt_urlök: deletedUrls.map(url => truncateUrl(url, 30))
          });
          return; // Kilépünk, ne állítsunk vissza semmit
        }
        
        console.log('Képeltérés észlelve!', {
          form: productImages.length,
          lokális: localImages.filter(img => !img.isTemp).length,
          hiányzó: missingUrls.length,
          extra: extraUrls.length
        });
        
        // Ha a form-ban több kép van, az a mérvadó
        if (productImages.length > localImages.filter(img => !img.isTemp).length) {
          // Ideiglenes képek megtartása, csak a stabilak cseréje
          const tempImages = localImages.filter(img => img.isTemp);
          setLocalImages([...productImages, ...tempImages]);
        } else if (productImages.length < localImages.filter(img => !img.isTemp).length && formUrls.length > 0) {
          // Ha lokálisan több kép van, frissítsük a form-ot
          setValue('images', localImages.filter(img => !img.isTemp));
        }
      }
    }
  }, [productImages, localImages, isUploading, setValue]);

  // Alert utáni kép helyreállítás figyelése
  const [alertShown, setAlertShown] = useState(false);
  const alertTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Ha képek eltűntek egy alert után
  useEffect(() => {
    // Visszaállítás, ha a képek váratlanul eltűnnek
    if (alertShown && localImages.length === 0 && lastUploadedImages.images.length > 0) {
      // ELLENŐRIZZÜK: ne állítsunk vissza képeket, ha éppen töröltük őket
      const hasDeletedImages = lastUploadedImages.deletedImageUrls.size > 0;
      
      if (hasDeletedImages) {
        console.log('Alert utáni képvisszaállítás megakadályozva - voltak törlések!');
        setAlertShown(false);
        return;
      }
      
      console.log('Alert utáni képvisszaállítás aktiválva');
      
      // Töröljük a korábbi időzítőt, ha van
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
      
      // Késleltetett visszaállítás
      alertTimeoutRef.current = setTimeout(() => {
        console.log('Képek visszaállítása az alert után');
        setLocalImages(lastUploadedImages.images);
        setValue('images', lastUploadedImages.images);
        setAlertShown(false);
      }, 200);
    }
    
    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
  }, [alertShown, localImages.length, setValue]);

  // Saját értesítés függvény, amely toast-ot használ alert helyett
  const notify = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    if (type === 'success') {
      toast.success(message);
    } else if (type === 'error') {
      toast.error(message);
    } else {
      toast(message);
    }
    
    // Toast kijelzés után ugyanúgy kezeljük az állapotot, mint az alert-nél tettük
    setAlertShown(true);
    setTimeout(() => {
      setAlertShown(false);
    }, 300);
  };

  // URL rövidítő segédfüggvény
  const truncateUrl = (url: string = '', maxLength: number = 30): string => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    
    const start = url.substring(0, maxLength / 2);
    const end = url.substring(url.length - maxLength / 2);
    return `${start}...${end}`;
  };
  
  // URL normalizáló segédfüggvény - eltávolítja a cache busting paramétereket
  const normalizeUrl = (url: string = ''): string => {
    if (!url) return '';
    // Eltávolítja a kérdőjel utáni összes query paramétert
    return url.split('?')[0];
  };
  
  // Kép azonosítása URL alapján, figyelmen kívül hagyva a cache busting paramétereket
  const isSameImage = (url1: string = '', url2: string = ''): boolean => {
    if (!url1 || !url2) return false;
    return normalizeUrl(url1) === normalizeUrl(url2);
  };
  
  // Form értékek betöltése - egyszerűsítve
  useEffect(() => {
    if (initialData) {
      // Form értékek beállítása
      Object.entries(initialData).forEach(([key, value]) => {
        setValue(key as keyof CreateProductFormData, value);
      });
      
      // Lokális állapotok beállítása
      if (initialData.thumbnailUrl) {
        setThumbnailUrl(initialData.thumbnailUrl);
      }
      
      // Csak akkor töltjük be a képeket, ha még nem voltak módosítva, vagy első betöltés
      if (initialData.images && initialData.images.length > 0 && 
          (!lastUploadedImages.hasChanges || lastUploadedImages.images.length === 0)) {
        console.log('Kezdeti képek betöltése:', initialData.images.length, 'db kép');
        
        // Mély másolat használata
        const initialImages = initialData.images.map(img => ({...img}));
        
        // Biztonságos főkép beállítás, ha kell
        if (!initialImages.some(img => img.isMain) && initialImages.length > 0) {
          initialImages[0].isMain = true;
          console.log('Nincs főkép a kezdeti képek között, az első kép beállítva főképként');
        }
        
        // URL-ek egyediesítése
        const urlMap = new Map<string, boolean>();
        const uniqueImages = initialImages.filter(img => {
          if (!img.url) return false;
          if (urlMap.has(img.url)) return false;
          // Törölt képek kiszűrése
          if (lastUploadedImages.deletedImageUrls.has(normalizeUrl(img.url))) return false;
          urlMap.set(img.url, true);
          return true;
        });
        
        // Ha voltak duplikációk, naplózzuk
        if (uniqueImages.length < initialImages.length) {
          console.warn('Duplikált képek találhatók a kezdeti betöltéskor! Eltávolítva.');
        }
        
        // Csak akkor állítjuk be, ha nincs lokális módosítás
        if (!lastUploadedImages.hasChanges) {
          setLocalImages(uniqueImages);
          setValue('images', uniqueImages);
          
          // Frissítjük a globális referenciát is
          lastUploadedImages.images = [...uniqueImages];
          lastUploadedImages.originalImageCount = uniqueImages.length;
          
          // Thumbnail beállítása a főkép alapján
          const mainImage = uniqueImages.find(img => img.isMain);
          if (mainImage && mainImage.url) {
            setThumbnailUrl(mainImage.url);
            setValue('thumbnailUrl', mainImage.url);
            lastUploadedImages.mainImageUrl = mainImage.url;
          }
        } else {
          console.log('Képek betöltése átugorva, mert lokális módosítások vannak. Lokális:', 
                    localImages.length, 'Server:', uniqueImages.length);
        }
      }
      
      // Hidden images loading - similar logic for hidden images
      if (initialData.hiddenImages && initialData.hiddenImages.length > 0 && 
          (!lastUploadedImages.hasChanges || lastUploadedImages.hiddenImages.length === 0)) {
        console.log('Kezdeti rejtett képek betöltése:', initialData.hiddenImages.length, 'db rejtett kép');
        
        // Mély másolat használata
        const initialHiddenImages = initialData.hiddenImages.map(img => ({...img}));
        
        // URL-ek egyediesítése
        const hiddenUrlMap = new Map<string, boolean>();
        const uniqueHiddenImages = initialHiddenImages.filter(img => {
          if (!img.url) return false;
          if (hiddenUrlMap.has(img.url)) return false;
          // Törölt képek kiszűrése
          if (lastUploadedImages.deletedHiddenImageUrls.has(normalizeUrl(img.url))) return false;
          hiddenUrlMap.set(img.url, true);
          return true;
        });
        
        // Ha voltak duplikációk, naplózzuk
        if (uniqueHiddenImages.length < initialHiddenImages.length) {
          console.warn('Duplikált rejtett képek találhatók a kezdeti betöltéskor! Eltávolítva.');
        }
        
        // Csak akkor állítjuk be, ha nincs lokális módosítás
        if (!lastUploadedImages.hasChanges) {
          setLocalHiddenImages(uniqueHiddenImages);
          setValue('hiddenImages', uniqueHiddenImages);
          
          // Frissítjük a globális referenciát is
          lastUploadedImages.hiddenImages = [...uniqueHiddenImages];
          lastUploadedImages.originalHiddenImageCount = uniqueHiddenImages.length;
        } else {
          console.log('Rejtett képek betöltése átugorva, mert lokális módosítások vannak. Lokális:', 
                    localHiddenImages.length, 'Server:', uniqueHiddenImages.length);
        }
      }
      
      // Type és isService szinkronizálása
      setValue('isService', initialData.type === 'service');
    }
  }, [initialData, setValue]);

  const productType = watch('type');
  const watchedCategory = watch('category') || {};
  const selectedMainCategory = watchedCategory.categoryId || '';
  
  // Get subcategories for the selected main category
  const availableSubcategories = categories.filter(cat => 
    cat.parentId === selectedMainCategory
  );

  // Type változáskor frissítjük az isService mezőt is
  useEffect(() => {
    setValue('isService', productType === 'service');
  }, [productType, setValue]);

  // Képek feltöltése
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    console.log('=== KÉPFELTÖLTÉS KEZDŐDIK ===');
    console.log('Fájlok száma:', files.length);
    console.log('Jelenlegi localImages:', localImages.length);

    setIsUploading(true);

    try {
      // Jelenlegi képek másolata (az eredeti képek, ideiglenes nélkül)
      const currentImages = localImages.filter(img => !img.isTemp);
      
      // Mentjük a jelenlegi form képeket is, hogy ne tűnjenek el
      const formImages = watch('images') || [];
      
      console.log('Jelenlegi állapot:', {
        currentImages: currentImages.length,
        formImages: formImages.length
      });
      
      // Összesített képek listája - a form képei és a jelenlegi lokális képek egyesítése
      // Használjunk Map helyett egyszerű deduplikációt
      const urlSet = new Set<string>();
      const allCurrentImages: TempProductImage[] = [];
      
      // Egyesítsük a két képforrást, url alapján deduplikálva
      [...currentImages, ...formImages].forEach(img => {
        if (img.url && !urlSet.has(img.url)) {
          urlSet.add(img.url);
          allCurrentImages.push(img);
        }
      });
      
      console.log('Edit mód képek egyesítve:', allCurrentImages.length);
      
      // Ideiglenes képek a felhasználói visszajelzéshez
      let withTempImages = [...allCurrentImages];
      const tempImages: TempProductImage[] = [];
      
      // Termék kód a fájlnévhez
      const code = watch('code') || 'temp_' + Date.now();
      const sanitizedCode = code.replace(/[^a-zA-Z0-9-_]/g, '_');
      
      console.log('Termék kód:', code, '-> Sanitized:', sanitizedCode);
      
      // Időbélyeg a csoportos feltöltéshez
      const batchTimestamp = Date.now();
      
      // Feldolgozzuk az összes kiválasztott fájlt
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        console.log(`Fájl ${i + 1}: ${file.name}, méret: ${Math.round(file.size/1024)}KB, típus: ${file.type}`);
        
        // Fájlméret ellenőrzés - max 5MB
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          console.warn(`A "${file.name}" fájl túl nagy (${Math.round(file.size/1024/1024)}MB), átugorva`);
          continue;
        }
        
        // Fájltípus ellenőrzése
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
          console.warn(`A "${file.name}" fájl nem támogatott típusú (${file.type}), átugorva`);
          continue;
        }
        
        // Lokális előnézet generálása
        const tempUrl = URL.createObjectURL(file);
        const tempId = `temp_${batchTimestamp}_${i}`;
        
        console.log(`Ideiglenes kép létrehozva: ${tempId}, URL: ${tempUrl.substring(0, 50)}...`);
        
        // Ideiglenes kép hozzáadása
        const tempImage = { url: tempUrl, isMain: false, isTemp: true, id: tempId, file } as TempProductImage & { file: File };
        tempImages.push(tempImage);
        withTempImages.push(tempImage);
      }
      
      console.log('Ideiglenes képek száma:', tempImages.length);
      console.log('Összes kép (régi + ideiglenes):', withTempImages.length);
      
      // Azonnal megjelenítjük a képeket (csak vizuálisan)
      setLocalImages(withTempImages);
      console.log('setLocalImages meghívva', withTempImages.length, 'képpel');
      
      // Párhuzamos feltöltések
      const uploadPromises = tempImages.map(async (tempImage) => {
        try {
          if (!tempImage.file) return null;
          
          // Egyedi fájlnév generálása időbélyeggel
          const file = tempImage.file;
          const originalFileName = file.name;
          const fileNameParts = originalFileName.split('.');
          const extension = fileNameParts.pop() || 'jpg';
          const baseName = fileNameParts.join('.');
          const newFileName = `${baseName}_${batchTimestamp}_${Math.floor(Math.random() * 1000)}.${extension}`;
          
          // Firebase feltöltési útvonal
          const uploadPath = `products/${sanitizedCode}/${newFileName}`;
          
          console.log(`Feltöltés kezdődik: ${originalFileName} -> ${uploadPath}`);
          
          // Feltöltés a Firebase Storage-ra
          const uploadedUrl = await uploadImage(file, uploadPath);
          
          console.log(`Sikeres feltöltés: ${originalFileName} -> ${uploadedUrl}`);
          
          // Új kép objektum az URL-lel a Storage-ból
          return { 
            tempId: tempImage.id,
            uploadedImage: {
              url: uploadedUrl, 
              isMain: false
            } as ProductImage 
          };
        } catch (error) {
          console.error(`Hiba a "${tempImage.file?.name}" fájl feltöltése közben:`, error);
          return { tempId: tempImage.id, error };
        }
      });
      
      console.log('Várakozás a feltöltésekre...');
      
      // Várjuk meg az összes feltöltést
      const results = await Promise.all(uploadPromises);
      
      console.log('Feltöltések befejezve, eredmények:', results);
      
      // A feltöltött képek és az eredeti nem ideiglenes képek kombinálása
      let updatedImages = [...allCurrentImages];
      let successCount = 0;
      
      // Lecseréljük az ideiglenes képeket a végleges képekre vagy eltávolítjuk őket hiba esetén
      results.forEach(result => {
        if (result && result.tempId && result.uploadedImage) {
          // Sikeres feltöltés
          updatedImages.push(result.uploadedImage);
          successCount++;
          console.log(`Sikeres kép hozzáadva: ${result.uploadedImage.url}`);
        } else if (result && result.error) {
          console.error(`Sikertelen feltöltés, tempId: ${result.tempId}`);
        }
      });
      
      console.log('Képfeltöltés eredménye:', {
        eredeti: allCurrentImages.length,
        új: successCount,
        összes: updatedImages.length,
        updatedImages: updatedImages.map(img => ({ url: img.url?.substring(0, 50), isMain: img.isMain }))
      });
      
      // Felszabadítjuk az ideiglenes URL-eket
      tempImages.forEach(img => {
        if (img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
      
      // Tájékoztatjuk a felhasználót
      let successMessage = '';
      if (successCount === files.length) {
        // Minden kép sikeresen feltöltve
        if (files.length === 1) {
          successMessage = 'A kép sikeresen feltöltve!';
        } else {
          successMessage = `Mind a ${files.length} kép sikeresen feltöltve!`;
        }
      } else if (successCount > 0) {
        // Részleges siker
        successMessage = `${successCount} kép feltöltve a ${files.length} képből. Néhány kép feltöltése nem sikerült.`;
      } else {
        // Minden feltöltés sikertelen volt
        throw new Error('Egyetlen kép feltöltése sem sikerült.');
      }
      
      // Másoljuk a végleges képeket, hogy biztos megmaradjanak
      const finalImages = [...updatedImages];
      
      console.log('Végleges képek beállítása:', finalImages.length);
      
      // Késleltetett többszörös ellenőrzés a megbízható működésért
      const checkAndFixImages = () => {
        const formImages = watch('images') || [];
        const currentLocalLength = localImages.length;
        
        console.log('checkAndFixImages hívva:', {
          form: formImages.length, 
          lokális: currentLocalLength, 
          várt: finalImages.length,
          globális_cache: lastUploadedImages.images.length
        });
        
        // Ha a form-ban nincsenek képek, de kellene lenniük
        if (formImages.length === 0 && finalImages.length > 0) {
          console.log('JAVÍTÁS: Form üres, de kellene lennie képeknek!');
          setValue('images', [...finalImages]);
        }
        
        // Ha a lokális állapotban nincsenek képek, de kellene lenniük
        if (currentLocalLength === 0 && finalImages.length > 0) {
          console.log('JAVÍTÁS: Lokális állapot üres, de kellene lennie képeknek!');
          setLocalImages([...finalImages]);
        }
        
        // Ha a form és a lokális állapot közötti eltérés
        if (formImages.length !== finalImages.length) {
          console.log('JAVÍTÁS: Form és várt mennyiség eltérés!');
          setValue('images', [...finalImages]);
        }
        
        // Ha van globális cache, de nincs helyi adat
        if (formImages.length === 0 && lastUploadedImages.images.length > 0) {
          console.log('JAVÍTÁS: Globális cache-ből visszaállítás!');
          setLocalImages([...lastUploadedImages.images]);
          setValue('images', [...lastUploadedImages.images]);
        }
        
        console.log('checkAndFixImages befejezve');
      };
      
      // FONTOS: Először beállítjuk az állapotokat még az alert előtt, majd véglegesen rögzítjük az állapotokat
      // 1. Azonnal frissítjük a form és a lokális állapotot
      console.log('=== ÁLLAPOT BEÁLLÍTÁS KEZDETE ===');
      console.log('setLocalImages meghívása finalImages-szel:', finalImages.length);
      console.log('finalImages részletei:', finalImages.map(img => ({ 
        url: img.url?.substring(0, 50), 
        isMain: img.isMain,
        isTemp: img.isTemp 
      })));
      
      setLocalImages(finalImages);
      
      console.log('setValue(images) meghívása finalImages-szel:', finalImages.length);
      setValue('images', finalImages);
      
      // A globális referenciában is megőrizzük
      lastUploadedImages.images = [...finalImages];
      lastUploadedImages.hasChanges = true;
      
      console.log('Globális cache frissítve:', lastUploadedImages.images.length);
      console.log('=== ÁLLAPOT BEÁLLÍTÁS VÉGE ===');
      
      console.log('=== KÉPFELTÖLTÉS BEFEJEZVE ===');
      
      // 2. Első ellenőrzés rövid várakozás után
      setTimeout(() => {
        const currentFormImages = watch('images') || [];
        const currentLocalImages = localImages.length;
        console.log('100ms után ellenőrzés:', {
          form: currentFormImages.length,
          lokális: currentLocalImages,
          várt: finalImages.length
        });
        checkAndFixImages();
      }, 100);
      
      // 3. Második ellenőrzés közepes várakozás után
      setTimeout(() => {
        const currentFormImages = watch('images') || [];
        console.log('500ms után ellenőrzés:', {
          form: currentFormImages.length,
          várt: finalImages.length
        });
        checkAndFixImages();
      }, 500);
      
      // 4. Csak hosszabb várakozás után jelenítjük meg az üzenetet
      setTimeout(() => {
        // Utolsó ellenőrzés az üzenet előtt
        const currentFormImages = watch('images') || [];
        console.log('Üzenet előtti ellenőrzés:', {
          form: currentFormImages.length,
          várt: finalImages.length
        });
        checkAndFixImages();
        
        // 5. Üzenet megjelenítése
        setTimeout(() => {
          // Saját notify függvényt használjuk, hogy nyomon követhessük
          setAlertShown(true);
          notify(successMessage);
          
          // 6. Még egyszer ellenőrizzük az alert után, hogy nem tűntek-e el a képek
          setTimeout(() => {
            // Extra ellenőrzés - ha a lokális képek teljesen eltűntek, használjuk a globális referenciát
            const formImages = watch('images') || [];
            console.log('Alert után ellenőrzés:', {
              form: formImages.length,
              globális_cache: lastUploadedImages.images.length
            });
            
            if (formImages.length === 0 && lastUploadedImages.images.length > 0) {
              console.log('KRITIKUS: A képek teljesen eltűntek, visszaállítás a biztonsági másolatból');
              setLocalImages([...lastUploadedImages.images]);
              setValue('images', [...lastUploadedImages.images]);
            } else {
              checkAndFixImages();
            }
            
            // 7. Végső ellenőrzés egy másodperc után
            setTimeout(() => {
              const finalCheck = watch('images') || [];
              console.log('Végső ellenőrzés 1s után:', {
                form: finalCheck.length,
                várt: finalImages.length
              });
              
              if (finalCheck.length !== finalImages.length) {
                console.log('VÉGSŐ JAVÍTÁS szükséges!');
                setLocalImages([...finalImages]);
                setValue('images', [...finalImages]);
              }
              
              checkAndFixImages();
              setAlertShown(false);
            }, 1000);
          }, 100);
        }, 100);
      }, 800);
      
    } catch (error) {
      console.error('Kép feltöltési hiba:', error);
      notify('Hiba történt a képek feltöltése közben.', 'error');
      
      // Hiba esetén visszaállítjuk az eredeti képeket
      const originalImages = localImages.filter(img => !img.isTemp);
      setLocalImages(originalImages);
      setValue('images', originalImages);
    } finally {
      setIsUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  // Kép beállítása fő képként
  const handleSetMainImage = (index: number) => {
    // Ellenőrizzük, hogy érvényes index-e
    if (index < 0 || index >= localImages.length) return;
    
    console.log('Főkép beállítása, index:', index);
    
    try {
      // Az új főkép URL azonosítása
      const newMainImageUrl = localImages[index]?.url;
      if (!newMainImageUrl) {
        console.error('Az új főképnek nincs URL-je');
        return;
      }
      
      console.log('Új főkép URL:', truncateUrl(newMainImageUrl, 30));
      
      // Készítünk egy mély másolatot a képekről
      const updatedImages = localImages.map(img => ({
        ...img,
        isMain: false // alapból minden kép nem főkép
      }));
      
      // Az új főkép beállítása
      updatedImages[index].isMain = true;
      
      // A thumbnail URL azonnal beállítása, hogy megelőzzük a versenyhelyzetet
      setThumbnailUrl(newMainImageUrl);
      setValue('thumbnailUrl', newMainImageUrl);
      
      console.log('Thumbnail URL beállítva:', truncateUrl(newMainImageUrl, 30));
      
      // Frissítjük a lokális képeket
      setLocalImages(updatedImages);
      
      // A form értékét is frissítjük - fontos, hogy a thumbnailUrl frissítés után történjen
      setTimeout(() => {
        setValue('images', JSON.parse(JSON.stringify(updatedImages)));
        
        // A globális mentésben is frissítjük
        lastUploadedImages.images = updatedImages.map(img => ({...img}));
        lastUploadedImages.mainImageUrl = newMainImageUrl;
        lastUploadedImages.hasChanges = true;
        lastUploadedImages.lastModified = Date.now();
        
        // Ellenőrizzük, hogy a form-ban is megfelelően frissültek-e az adatok
        setTimeout(() => {
          const formImages = watch('images') || [];
          const mainImage = formImages.find(img => img.isMain);
          const thumbnailUrlValue = watch('thumbnailUrl');
          
          console.log('Főkép ellenőrzés:', {
            főkép_index: formImages.findIndex(img => img.isMain),
            thumbnailUrl: truncateUrl(thumbnailUrlValue || '', 30),
            főkép_url: mainImage ? truncateUrl(mainImage.url || '', 30) : 'nincs'
          });
          
          // Ha a thumbnailUrl és a főkép URL nem egyezik, újra beállítjuk
          if (mainImage?.url && thumbnailUrlValue !== mainImage.url) {
            console.warn('Thumbnail és főkép URL eltérés! Újraállítás...');
            setValue('thumbnailUrl', mainImage.url);
            setThumbnailUrl(mainImage.url);
          }
          
          // ELTÁVOLÍTVA: Automatikus mentés - csak a sikeres ellenőrzés után
          // notify('Főkép módosítva, automatikus mentés...');
          // setTimeout(() => {
          //   // Csak a képadatokat mentjük el
          //   saveImagesOnly();
          // }, 500);
        }, 100);
      }, 0);
      
      // Vizuális visszajelzés a felhasználónak
      notify('A kép sikeresen beállítva fő képként és thumbnail-ként! Kattints a "Mentés bezárás nélkül" gombra a változtatások elmentéséhez.');
      
    } catch (error) {
      console.error('Hiba a főkép beállítása közben:', error);
      notify('Hiba történt a főkép beállítása közben.', 'error');
    }
  };

  // Thumbnail URL szinkronizálása a főképpel
  useEffect(() => {
    // Kiszűrjük az ideiglenes képeket a döntéshozatalhoz
    const stableImages = localImages.filter(img => !img.isTemp);
    
    // Ha van stabil kép, mindig annak az URL-jét használjuk thumbnailként
    if (stableImages.length > 0) {
      // Keresünk főképet a stabil képek között
      const mainImage = stableImages.find(img => img.isMain);
      
      if (mainImage && mainImage.url) {
        // Ha az új érték különbözik a mostanitól, frissítjük
        if (thumbnailUrl !== mainImage.url) {
          console.log('Thumbnail szinkronizálás:', truncateUrl(mainImage.url, 30));
          setThumbnailUrl(mainImage.url);
          setValue('thumbnailUrl', mainImage.url);
        }
      } else if (stableImages.length > 0 && !mainImage) {
        // Ha nincs főkép, de van stabil kép, akkor az elsőt állítjuk be főképként
        const updatedImages = stableImages.map((img, idx) => ({
          ...img, 
          isMain: idx === 0  // Csak az első legyen fő
        }));
        
        // A teljes képkészletet frissítjük, megtartva az ideiglenes képeket
        const tempImages = localImages.filter(img => img.isTemp);
        const newImages = [...updatedImages, ...tempImages];
        
        console.log('Nincs főkép, automatikus beállítás az első képre');
        
        // Frissítjük mindkét helyen
        setLocalImages(newImages);
        setValue('images', updatedImages); // Form-ba csak a stabil képek kerülnek
        
        // Thumbnail frissítése is
        if (updatedImages[0]?.url) {
          setThumbnailUrl(updatedImages[0].url);
          setValue('thumbnailUrl', updatedImages[0].url);
        }
      }
    } else {
      // Ha nincs stabil kép, töröljük a thumbnail-t (függetlenül attól, hogy van-e ideiglenes kép)
      if (thumbnailUrl) {
        console.log('Nincs stabil kép, thumbnail törlése');
        setThumbnailUrl('');
        setValue('thumbnailUrl', '');
      }
    }
  }, [localImages, thumbnailUrl, setValue, watch]);

  // Képek állapotváltozásának extra figyelése - előzi meg a képek eltűnését/visszatérését
  useEffect(() => {
    // Csak akkor ellenőrizzük, ha vannak képeink
    if (localImages.length > 0) {
      // Ellenőrizzük, hogy minden kép egyedi URL-lel rendelkezik-e
      const urls = localImages.map(img => img.url).filter(Boolean) as string[];
      const uniqueUrls = Array.from(new Set(urls));
      
      // Ha van duplikáció, megszüntetjük
      if (uniqueUrls.length < urls.length) {
        console.warn('Duplikált képek találhatók! Egyediesítés...');
        const uniqueImages: TempProductImage[] = [];
        const seenUrls: Record<string, boolean> = {};
        
        // Csak az egyedi URL-lel rendelkező képeket tartjuk meg
        localImages.forEach(img => {
          if (img.url && !seenUrls[img.url]) {
            seenUrls[img.url] = true;
            uniqueImages.push({...img});
          }
        });
        
        // Frissítjük a képeket
        setLocalImages(uniqueImages);
        setValue('images', uniqueImages.filter(img => !img.isTemp));
      }
      
      // Ellenőrizzük, hogy a feketelistán lévő képek ne kerüljenek vissza
      if (lastUploadedImages.deletedImageUrls.size > 0) {
        const deletedUrls = Array.from(lastUploadedImages.deletedImageUrls);
        const hasDeletedImages = localImages.some(img => 
          img.url && deletedUrls.some(deletedUrl => isSameImage(img.url, deletedUrl))
        );
        
        if (hasDeletedImages) {
          console.warn('Törölt képek visszakerültek! Eltávolítás...');
          const cleanedImages = localImages.filter(img => 
            !img.url || !deletedUrls.some(deletedUrl => isSameImage(img.url, deletedUrl))
          );
          
          setLocalImages(cleanedImages);
          setValue('images', cleanedImages.filter(img => !img.isTemp));
        }
      }
      
      // Minden kép csak egyszer szerepelhet, amelyik kép kikerült, az ne jöjjön vissza
      // Minden mentett állapotot ellenőrzünk és frissítünk
      const formImages = watch('images') || [];
      
      // Ha bármelyik állapotban nincs főkép, állítsunk be egyet
      if (!localImages.some(img => img.isMain) && localImages.length > 0) {
        const updatedWithMain = localImages.map((img, idx) => ({
          ...img, 
          isMain: idx === 0
        }));
        setLocalImages(updatedWithMain);
      }
      
      if (!formImages.some(img => img.isMain) && formImages.length > 0) {
        const updatedWithMain = formImages.map((img, idx) => ({
          ...img, 
          isMain: idx === 0
        }));
        setValue('images', updatedWithMain);
      }
    }
  }, [localImages, setValue, watch]);

  // Kép törlése - a tényleges törlési folyamat
  const handleRemoveImage = () => {
    // Ellenőrizzük, hogy van-e kiválasztott kép törlésre
    if (imageToDeleteIndex === null) {
      console.error('Nincs kiválasztott kép a törléshez');
      return;
    }
    
    const index = imageToDeleteIndex;
    console.log('Kép törlése elkezdődött:', index);
    
    // Eldöntjük, hogy normál vagy rejtett képet kell törölni
    // Ha az index a normál képek tartományában van, akkor normál képet törlünk
    // Ha túlmutat azon, akkor rejtett képet
    if (index < localImages.length) {
      // Normál kép törlése
      console.log('Normál kép törlése, index:', index);
      console.log('Törlés előtti képek:', localImages.length, 'db');
      
      try {
        // Az eltávolítandó kép URL-jének azonosítása
        const imageToDeleteUrl = localImages[index]?.url;
        if (!imageToDeleteUrl) {
          console.error('Nincs URL a törölni kívánt képhez:', index);
          return;
        }
        
        // Normalizáljuk az URL-t a cache busting paraméterek nélkül
        const normalizedDeleteUrl = normalizeUrl(imageToDeleteUrl);
        console.log('Törlendő kép URL:', truncateUrl(normalizedDeleteUrl, 30));
        
        // Adjuk hozzá a törölt URL-t a feketelistához
        lastUploadedImages.deletedImageUrls.add(normalizedDeleteUrl);
        lastUploadedImages.hasChanges = true;
        lastUploadedImages.lastModified = Date.now();
        
        // URL alapú eltávolítás - ez biztonságosabb mint index alapú
        const remainingImages = localImages.filter(img => !isSameImage(img.url, imageToDeleteUrl));
        
        // Ellenőrizzük, hogy tényleg eltávolítottuk-e a képet
        if (remainingImages.length >= localImages.length) {
          console.error('A kép nem lett eltávolítva! Várt hossz:', localImages.length - 1, 'aktuális:', remainingImages.length);
          // Próbáljuk index alapú törléssel
          const indexBasedFiltered = [...localImages];
          indexBasedFiltered.splice(index, 1);
          remainingImages.length = 0; // Töröljük a tartalmat
          indexBasedFiltered.forEach(img => remainingImages.push({...img})); // Új objektumok hozzáadása
        }
        
        console.log('Törlés után maradó képek:', remainingImages.length, 'db');
        
        // Ellenőrizzük, hogy a törölt kép fő kép volt-e
        const wasMainImage = localImages[index]?.isMain || false;
        
        // Ha a törölt kép fő kép volt, és van még kép, akkor az elsőt állítjuk be fő képként
        if (wasMainImage && remainingImages.length > 0) {
          remainingImages[0].isMain = true;
          const newMainUrl = remainingImages[0].url || '';
          console.log('Új főkép beállítva:', truncateUrl(newMainUrl, 30));
          
          // Thumbnail frissítése is
          setThumbnailUrl(newMainUrl);
          setValue('thumbnailUrl', newMainUrl);
        } else if (remainingImages.length === 0) {
          // Ha nincs több kép, töröljük a thumbnail-t is - MINDEN helyen!
          console.log('Nincs több kép, thumbnail törölve MINDEN helyről');
          setThumbnailUrl('');
          setValue('thumbnailUrl', '');
          
          // Globális cache-ben is töröljük
          lastUploadedImages.mainImageUrl = '';
        }

        // Frissítések - FONTOS: a setValue előbb történjen, mint a setLocalImages
        // mert a React 18 batch frissítésénél nem garantált a sorrend
        setValue('images', JSON.parse(JSON.stringify(remainingImages))); // Mély másolat
        lastUploadedImages.images = remainingImages.map(img => ({...img})); // Mély másolat 

        // Azonnal megerősítés
        setTimeout(() => {
          const formImages = watch('images') || [];
          console.log('Azonnali ellenőrzés - képek száma form:', formImages.length);
          
          // Ha nincs kép, biztosítsuk hogy a thumbnail is üres legyen
          if (formImages.length === 0) {
            console.log('Form ellenőrzés: Nincs kép, thumbnail explicit törlése');
            setThumbnailUrl('');
            setValue('thumbnailUrl', '');
          }
          
          // Csak a setLocalImages hívás után módosítjuk a DOM-ot
          setLocalImages(remainingImages.map(img => ({...img}))); // Újabb mély másolat
          
          // Erre van szükség, hogy a React biztosan újra renderlje a komponenst
          setTimeout(() => {
            const afterLocalUpdate = watch('images') || [];
            console.log('LocalState update után - képek:', afterLocalUpdate.length);
            
            // Még egy ellenőrzés a thumbnail-ra
            if (afterLocalUpdate.length === 0) {
              console.log('LocalState ellenőrzés: Nincs kép, thumbnail explicit törlése');
              setThumbnailUrl('');
              setValue('thumbnailUrl', '');
            }
          }, 0);
        }, 0);
        
        // Végső megerősítés
        setTimeout(() => {
          // Még egyszer megnézzük a form értéket
          const finalFormCheck = watch('images') || [];
          
          // Ha valami módon visszakerült a kép, erőszakosan eltávolítjuk újra
          if (finalFormCheck.some(img => isSameImage(img.url, imageToDeleteUrl))) {
            console.warn('A kép visszakerült! Újra töröljük...');
            const forcedCleanup = finalFormCheck.filter(img => !isSameImage(img.url, imageToDeleteUrl));
            setValue('images', forcedCleanup);
            setLocalImages(forcedCleanup);
            lastUploadedImages.images = [...forcedCleanup];
          }
          
          // Végső thumbnail ellenőrzés - ha nincs kép, legyen üres
          if (finalFormCheck.length === 0) {
            console.log('Végső ellenőrzés: Nincs kép, thumbnail explicit törlése');
            setThumbnailUrl('');
            setValue('thumbnailUrl', '');
            lastUploadedImages.mainImageUrl = '';
          }
          
          console.log('Végső ellenőrzés - képek:', watch('images')?.length, 'lokális:', localImages.length, 'thumbnail:', watch('thumbnailUrl') ? 'VAN' : 'NINCS');
        }, 300);
        
        // Felhasználói visszajelzés
        console.log('Kép törlés befejeződött:', index);
        notify('Kép sikeresen törölve!');
      } catch (error) {
        console.error('Hiba a kép törlése közben:', error);
        notify('Hiba történt a kép törlése közben.', 'error');
      }
    } else {
      // Rejtett kép törlése
      const hiddenIndex = index - localImages.length;
      console.log('Rejtett kép törlése, rejtett index:', hiddenIndex);
      handleRemoveHiddenImage(hiddenIndex);
      notify('Rejtett kép sikeresen törölve!');
    }
    
    return false; // Megakadályozzuk az esetleges buborékolást
  };

  // A form feldolgozása előtt kiegészítjük a hiányzó mezőket
  const handleFormSubmit = (data: CreateProductFormData) => {
    // Ellenőrizzük a kód validációt
    if (!codeValidation.isValid) {
      notify(t('products.validation.codeAlreadyInUse'), 'error');
      return;
    }

    // Ellenőrizzük, hogy nem fut-e még kód ellenőrzés
    if (codeValidation.isChecking) {
      notify(t('products.validation.waitForCodeCheck'), 'warning');
      return;
    }

    // Győződjünk meg, hogy az isService mező szinkronban van a type mezővel
    data.isService = data.type === 'service';
    
    // Csomag adatok hozzáadása, ha package típus
    if (data.type === 'package') {
      data.packageDetails = {
        services: packageServices,
        ...packageDetails
      };
    }
    
    console.log('=== FORM SUBMISSION KEZDŐDIK ===');
    console.log('Form feldolgozás kezdődik, jelenlegi képek:', {
      localImages: localImages.length,
      formImages: (data.images || []).length,
      localImagesDetail: localImages.map(img => ({ 
        url: img.url?.substring(0, 50), 
        isMain: img.isMain, 
        isTemp: img.isTemp 
      }))
    });
    
    // A lokális képek használata elsődleges forrásként, mivel ott garantált a helyes törlés
    // Csak a stabil (nem ideiglenes) képeket használjuk
    const stableImages = localImages.filter(img => !img.isTemp).map(img => ({
      url: img.url || '',
      isMain: img.isMain || false,
      // További mezőket csak akkor adjuk hozzá, ha léteznek
      ...(img.title && { title: img.title }),
      ...(img.alt && { alt: img.alt }),
      ...(img.sortOrder && { sortOrder: img.sortOrder })
    }));
    
    console.log('Mentés előtti stabil képek:', stableImages.length);
    console.log('Stabil képek részletei:', stableImages.map(img => ({ 
      url: img.url?.substring(0, 50), 
      isMain: img.isMain 
    })));
    
    // Képek hozzáadása a form adatokhoz
    if (stableImages.length > 0) {
      data.images = stableImages;
      
      // Ellenőrizzük, hogy van-e főkép
      const hasMainImage = stableImages.some(img => img.isMain);
      
      // Ha nincs főkép, az elsőt állítsuk be főképként
      if (!hasMainImage && stableImages.length > 0) {
        data.images[0].isMain = true;
        console.log('Nincs főkép, az első kép beállítva főképként');
      }
      
      // Keressük meg a főkép URL-jét
      const mainImage = data.images.find(img => img.isMain);
      
      // Biztosítjuk, hogy a thumbnail URL megegyezzen a főkép URL-jével
      if (mainImage && mainImage.url) {
        data.thumbnailUrl = mainImage.url;
        console.log('Thumbnail URL beállítva a főkép URL-jére:', truncateUrl(mainImage.url, 30));
      } else if (data.images.length > 0) {
        // Ha nincs főkép, de van kép, az első kép URL-jét használjuk
        data.thumbnailUrl = data.images[0].url;
        data.images[0].isMain = true;
        console.log('Nincs főkép, thumbnail az első kép URL-jére állítva:', truncateUrl(data.images[0].url, 30));
      }
    } else {
      // NINCS KÉP ESETÉN: explicit és teljes törlés
      data.images = []; // Üres tömb
      data.thumbnailUrl = ''; // Üres string - FONTOS!
      
      // Lokális állapotok tisztítása is
      setThumbnailUrl('');
      setValue('thumbnailUrl', '');
      
      // Globális cache tisztítása
      lastUploadedImages.mainImageUrl = '';
      
      console.log('FIGYELEM: Nincsenek stabil képek - MINDEN thumbnail adat törölve!');
    }
    
    // Debug - ellenőrizzük, hogy a képadatok helyesen vannak-e formázva
    console.log('Mentés előtti képadatok:', {
      thumbnailUrl: data.thumbnailUrl ? truncateUrl(data.thumbnailUrl, 30) : 'NINCS/ÜRES',
      képek_száma: data.images.length,
      főkép_van: data.images.some(img => img.isMain),
      összes_adat: data.images.map(img => ({ url: img.url?.substring(0, 50), isMain: img.isMain }))
    });
    
    // Rejtett képek feldolgozása
    const stableHiddenImages = localHiddenImages.filter(img => !img.isTemp).map(img => ({
      url: img.url || '',
      isMain: img.isMain || false,
      // További mezőket csak akkor adjuk hozzá, ha léteznek
      ...(img.title && { title: img.title }),
      ...(img.alt && { alt: img.alt }),
      ...(img.sortOrder && { sortOrder: img.sortOrder })
    }));
    
    console.log('Mentés előtti stabil rejtett képek:', stableHiddenImages.length);
    
    // Rejtett képek hozzáadása a form adatokhoz
    if (stableHiddenImages.length > 0) {
      data.hiddenImages = stableHiddenImages;
    } else {
      data.hiddenImages = []; // Üres tömb, ha nincs rejtett kép
    }
    
    console.log('=== VÉGSŐ ADATOK KÜLDÉSE ===');
    console.log('data.images hossza:', data.images.length);
    console.log('data.hiddenImages hossza:', data.hiddenImages?.length || 0);
    console.log('data.thumbnailUrl:', data.thumbnailUrl ? 'VAN' : 'NINCS/ÜRES');
    
    // Callback hívása a kiegészített adatokkal
    onSubmit(data);
  };

  // Mentés bezárás nélkül
  const handleSaveWithoutClose = () => {
    // Validálja és elküldi a formot, majd megakadályozza a bezárást
    handleSubmit((data) => {
      // Győződjünk meg, hogy az isService mező szinkronban van a type mezővel
      data.isService = data.type === 'service';
      
      // Előkészítjük a képeket
      const stableImages = localImages.filter(img => !img.isTemp).map(img => ({
        url: img.url || '',
        isMain: img.isMain || false,
        ...(img.title && { title: img.title }),
        ...(img.alt && { alt: img.alt }),
        ...(img.sortOrder && { sortOrder: img.sortOrder })
      }));
      
      // Képek hozzáadása a form adatokhoz
      if (stableImages.length > 0) {
        data.images = stableImages;
        
        // Ha nincs főkép, állítsuk be az elsőt főképként
        if (!stableImages.some(img => img.isMain) && stableImages.length > 0) {
          data.images[0].isMain = true;
        }
        
        // Főkép URL beállítása thumbnailként
        const mainImage = data.images.find(img => img.isMain);
        if (mainImage && mainImage.url) {
          data.thumbnailUrl = mainImage.url;
        } else if (data.images.length > 0) {
          data.thumbnailUrl = data.images[0].url;
          data.images[0].isMain = true;
        }
      } else {
        // NINCS KÉP ESETÉN: explicit és teljes törlés itt is
        data.images = [];
        data.thumbnailUrl = '';
        
        // Lokális állapotok tisztítása is
        setThumbnailUrl('');
        setValue('thumbnailUrl', '');
        
        // Globális cache tisztítása
        lastUploadedImages.mainImageUrl = '';
        
        console.log('SaveWithoutClose: Nincsenek képek - MINDEN thumbnail adat törölve!');
      }
      
      // Rejtett képek feldolgozása saveWithoutClose esetén is
      const stableHiddenImages = localHiddenImages.filter(img => !img.isTemp).map(img => ({
        url: img.url || '',
        isMain: img.isMain || false,
        ...(img.title && { title: img.title }),
        ...(img.alt && { alt: img.alt }),
        ...(img.sortOrder && { sortOrder: img.sortOrder })
      }));
      
      if (stableHiddenImages.length > 0) {
        data.hiddenImages = stableHiddenImages;
      } else {
        data.hiddenImages = [];
      }
      
      // Use the appropriate callback
      onSubmit(data);
      
      // Mivel most már elmentettük a változtatásokat, frissítsük a hasChanges állapotot
      lastUploadedImages.hasChanges = false;
      
      // Értesítés a felhasználónak, hogy a mentés sikeres volt, de a popup nyitva maradt
      notify('Adatok sikeresen mentve, a popup nyitva maradt.', 'success');
    })();
  };

  // Kisebb segédfüggvény a CORS vagy egyéb hibák elkerülésére - egyszerűsítve
  const addCacheBuster = (url: string | undefined): string => {
    if (!url) return '';
    
    // Ha már van query paraméter, akkor & jellel adjuk hozzá, különben ? jellel
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${Date.now()}`;
  };

  // Képek állapotváltozásának figyelése
  useEffect(() => {
    // Csak akkor aktiválódik, ha nem feltöltés közben történik a változás
    if (!isUploading && !localImages.some(img => img.isTemp)) {
      // Korábbi képek száma a globális változóban
      const previousImageCount = lastUploadedImages.images.length;
      
      // Jelenlegi képek száma
      const currentImageCount = localImages.length;
      
      // Ha a képek száma váratlanul csökkent, és nem kézileg töröltük őket
      if (previousImageCount > 0 && currentImageCount === 0) {
        // ELLENŐRIZZÜK: ne állítsunk vissza képeket, ha éppen töröltük őket
        const hasDeletedImages = lastUploadedImages.deletedImageUrls.size > 0;
        
        if (hasDeletedImages) {
          console.log('Képek eltűntek, de törlések történtek - visszaállítás megakadályozva!');
          return;
        }
        
        console.log('A képek eltűntek! Visszaállítás...');
        // Visszaállítjuk a globális állapotból
        setLocalImages(lastUploadedImages.images);
        setValue('images', lastUploadedImages.images);
      }
    }
  }, [isUploading, localImages, setValue]);

  // Thumbnail URL külön monitorozása
  useEffect(() => {
    // Csak akkor fusson, ha van értékes thumbnailUrl
    if (thumbnailUrl) {
      // Biztosítsuk, hogy a form-ban is megfelelően beállításra került
      const currentFormThumbUrl = watch('thumbnailUrl');
      
      if (thumbnailUrl !== currentFormThumbUrl) {
        console.log('ThumbnailUrl szinkronizálás form értékre:', truncateUrl(thumbnailUrl, 30));
        setValue('thumbnailUrl', thumbnailUrl);
      }
      
      // Ellenőrizzük, hogy a főkép is konzisztens legyen a thumbnail-lel
      const images = watch('images') || [];
      const mainImageIndex = images.findIndex(img => img.isMain);
      
      // Ha van főkép, ellenőrizzük, hogy annak URL-je megegyezik-e a thumbnail URL-jével
      if (mainImageIndex >= 0 && images[mainImageIndex].url !== thumbnailUrl) {
        console.warn('Főkép és thumbnail inkonzisztencia! Főkép URL frissítése...');
        
        // Frissítjük a főkép URL-jét a form-ban
        const updatedImages = images.map(img => ({
          ...img,
          isMain: false // először minden kép nem főkép
        }));
        
        // Keressük meg azt a képet, amelynek URL-je megegyezik a thumbnail URL-jével
        const matchingImageIndex = images.findIndex(img => img.url === thumbnailUrl);
        
        if (matchingImageIndex >= 0) {
          // Ha találtunk egyező képet, azt állítjuk be főképként
          updatedImages[matchingImageIndex].isMain = true;
          console.log('Új főkép beállítva az egyező thumbnail alapján, index:', matchingImageIndex);
          
          // Frissítjük a form értékét
          setValue('images', updatedImages);
          
          // Frissítjük a lokális képeket is
          const localUpdated = localImages.map(img => ({
            ...img,
            isMain: isSameImage(img.url, thumbnailUrl)
          }));
          
          setLocalImages(localUpdated);
          
          // A globális mentést is frissítjük
          lastUploadedImages.images = [...localUpdated];
        }
      }
    }
  }, [thumbnailUrl, setValue, watch]);

  // Új védő mechanizmus a képadatok megőrzéséhez
  useEffect(() => {
    // Háttérlekérdezések ellen: ha vannak lokális módosítások és a watch-ban más adat jelenik meg
    // mint amit mi állítottunk be, akkor visszaállítjuk a saját adatainkat
    const handleBackgroundRefetch = () => {
      if (lastUploadedImages.hasChanges) {
        // ELLENŐRIZZÜK: ne állítsunk vissza képeket, ha éppen töröltük őket
        const hasDeletedImages = lastUploadedImages.deletedImageUrls.size > 0;
        
        if (hasDeletedImages) {
          console.log('Háttérlekérdezés visszaállítás megakadályozva - voltak törlések!');
          return;
        }
        
        const formImages = watch('images') || [];
        
        // Nem egyezik a képek száma vagy a főkép
        const needsSync = 
          formImages.length !== lastUploadedImages.images.length ||
          (lastUploadedImages.mainImageUrl && 
           !formImages.some(img => img.isMain && img.url === lastUploadedImages.mainImageUrl));
        
        if (needsSync) {
          console.log('Háttérlekérdezés érzékelve! Képek visszaállítása a lokális állapotból', {
            form: formImages.length,
            lokális: lastUploadedImages.images.length
          });
          
          // Visszaállítjuk a saját képeinket
          setValue('images', JSON.parse(JSON.stringify(lastUploadedImages.images)));
          
          // A lokális képeket is frissítjük
          setLocalImages(lastUploadedImages.images.map(img => ({...img})));
          
          // Főkép URL-t is visszaállítjuk
          if (lastUploadedImages.mainImageUrl) {
            setValue('thumbnailUrl', lastUploadedImages.mainImageUrl);
            setThumbnailUrl(lastUploadedImages.mainImageUrl);
          }
        }
      }
    };
    
    // Időzített ellenőrzés háttérlekérdezések észlelésére
    const intervalId = setInterval(handleBackgroundRefetch, 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [watch, setValue]);

  // Kép törlési kérés - megjeleníti a megerősítő dialógust
  const handleImageDeleteRequest = (index: number) => {
    // Ellenőrizzük, hogy érvényes index-e
    if (index < 0 || index >= localImages.length) {
      console.error('Érvénytelen index:', index);
      return;
    }
    
    const imgToDelete = localImages[index];
    if (!imgToDelete || !imgToDelete.url) {
      console.error('Nincs URL a törölni kívánt képhez:', index);
      return;
    }
    
    // Beállítjuk a törlendő kép adatait
    setImageToDeleteIndex(index);
    setImageToDeleteUrl(imgToDelete.url);
    setIsDeleteModalOpen(true);
  };

  // Rejtett képek feltöltése - hasonló a normál képekhez, de külön kezelés
  const handleHiddenImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingHidden(true);

    try {
      // Jelenlegi rejtett képek másolata (az eredeti képek, ideiglenes nélkül)
      const currentHiddenImages = localHiddenImages.filter(img => !img.isTemp);
      
      // Mentjük a jelenlegi form rejtett képeket is, hogy ne tűnjenek el
      const formHiddenImages = watch('hiddenImages') || [];
      
      // Összesített rejtett képek listája
      const urlSet = new Set<string>();
      const allCurrentHiddenImages: TempProductImage[] = [];
      
      // Egyesítsük a két képforrást, url alapján deduplikálva
      [...currentHiddenImages, ...formHiddenImages].forEach(img => {
        if (img.url && !urlSet.has(img.url)) {
          urlSet.add(img.url);
          allCurrentHiddenImages.push(img);
        }
      });
      
      console.log('Rejtett képek egyesítve:', allCurrentHiddenImages.length);
      
      // Ideiglenes képek a felhasználói visszajelzéshez
      let withTempHiddenImages = [...allCurrentHiddenImages];
      const tempHiddenImages: TempProductImage[] = [];
      
      // Termék kód a fájlnévhez
      const code = watch('code') || 'temp_' + Date.now();
      const sanitizedCode = code.replace(/[^a-zA-Z0-9-_]/g, '_');
      
      // Időbélyeg a csoportos feltöltéshez
      const batchTimestamp = Date.now();
      
      // Feldolgozzuk az összes kiválasztott fájlt
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Fájlméret ellenőrzés - max 5MB
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          console.warn(`A "${file.name}" fájl túl nagy (${Math.round(file.size/1024/1024)}MB), átugorva`);
          continue;
        }
        
        // Fájltípus ellenőrzése
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
          console.warn(`A "${file.name}" fájl nem támogatott típusú (${file.type}), átugorva`);
          continue;
        }
        
        // Lokális előnézet generálása
        const tempUrl = URL.createObjectURL(file);
        const tempId = `temp_hidden_${batchTimestamp}_${i}`;
        
        // Ideiglenes kép hozzáadása
        const tempImage = { url: tempUrl, isMain: false, isTemp: true, id: tempId, file } as TempProductImage & { file: File };
        tempHiddenImages.push(tempImage);
        withTempHiddenImages.push(tempImage);
      }
      
      // Azonnal megjelenítjük a rejtett képeket (csak vizuálisan)
      setLocalHiddenImages(withTempHiddenImages);
      
      // Párhuzamos feltöltések
      const uploadPromises = tempHiddenImages.map(async (tempImage) => {
        try {
          if (!tempImage.file) return null;
          
          // Egyedi fájlnév generálása időbélyeggel - hidden prefix
          const file = tempImage.file;
          const originalFileName = file.name;
          const fileNameParts = originalFileName.split('.');
          const extension = fileNameParts.pop() || 'jpg';
          const baseName = fileNameParts.join('.');
          const newFileName = `hidden_${baseName}_${batchTimestamp}_${Math.floor(Math.random() * 1000)}.${extension}`;
          
          // Firebase feltöltési útvonal - hidden subfolder
          const uploadPath = `products/${sanitizedCode}/hidden/${newFileName}`;
          
          // Feltöltés a Firebase Storage-ra
          const uploadedUrl = await uploadImage(file, uploadPath);
          
          // Új kép objektum az URL-lel a Storage-ból
          return { 
            tempId: tempImage.id,
            uploadedImage: {
              url: uploadedUrl, 
              isMain: false
            } as ProductImage 
          };
        } catch (error) {
          console.error(`Hiba a "${tempImage.file?.name}" rejtett kép feltöltése közben:`, error);
          return { tempId: tempImage.id, error };
        }
      });
      
      // Várjuk meg az összes feltöltést
      const results = await Promise.all(uploadPromises);
      
      // A feltöltött rejtett képek és az eredeti nem ideiglenes képek kombinálása
      let updatedHiddenImages = [...allCurrentHiddenImages];
      let successCount = 0;
      
      // Lecseréljük az ideiglenes képeket a végleges képekre vagy eltávolítjuk őket hiba esetén
      results.forEach(result => {
        if (result && result.tempId && result.uploadedImage) {
          // Sikeres feltöltés
          updatedHiddenImages.push(result.uploadedImage);
          successCount++;
        }
      });
      
      console.log('Rejtett képfeltöltés eredménye:', {
        eredeti: allCurrentHiddenImages.length,
        új: successCount,
        összes: updatedHiddenImages.length
      });
      
      // Felszabadítjuk az ideiglenes URL-eket
      tempHiddenImages.forEach(img => {
        if (img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
      
      // Tájékoztatjuk a felhasználót
      let successMessage = '';
      if (successCount === files.length) {
        // Minden kép sikeresen feltöltve
        if (files.length === 1) {
          successMessage = 'A rejtett kép sikeresen feltöltve!';
        } else {
          successMessage = `Mind a ${files.length} rejtett kép sikeresen feltöltve!`;
        }
      } else if (successCount > 0) {
        // Részleges siker
        successMessage = `${successCount} rejtett kép feltöltve a ${files.length} képből. Néhány kép feltöltése nem sikerült.`;
      } else {
        // Minden feltöltés sikertelen volt
        throw new Error('Egyetlen rejtett kép feltöltése sem sikerült.');
      }
      
      // Frissítjük az állapotokat
      setLocalHiddenImages(updatedHiddenImages);
      setValue('hiddenImages', updatedHiddenImages.filter(img => !img.isTemp));
      
      // A globális referenciában is megőrizzük
      lastUploadedImages.hiddenImages = [...updatedHiddenImages];
      lastUploadedImages.hasChanges = true;
      
      // Üzenet megjelenítése
      notify(successMessage);
      
    } catch (error) {
      console.error('Rejtett kép feltöltési hiba:', error);
      notify('Hiba történt a rejtett képek feltöltése közben.', 'error');
      
      // Hiba esetén visszaállítjuk az eredeti képeket
      const originalHiddenImages = localHiddenImages.filter(img => !img.isTemp);
      setLocalHiddenImages(originalHiddenImages);
      setValue('hiddenImages', originalHiddenImages);
    } finally {
      setIsUploadingHidden(false);
      if (e.target) e.target.value = '';
    }
  };

  // Rejtett kép törlési kérés - megjeleníti a megerősítő dialógust
  const handleHiddenImageDeleteRequest = (adjustedIndex: number) => {
    // Az adjusted index-ből kiszámítjuk a valódi rejtett kép indexét
    const hiddenIndex = adjustedIndex - localImages.length;
    
    // Ellenőrizzük, hogy érvényes index-e
    if (hiddenIndex < 0 || hiddenIndex >= localHiddenImages.length) {
      console.error('Érvénytelen rejtett kép index:', hiddenIndex, 'adjusted:', adjustedIndex);
      return;
    }
    
    const imgToDelete = localHiddenImages[hiddenIndex];
    if (!imgToDelete || !imgToDelete.url) {
      console.error('Nincs URL a törölni kívánt rejtett képhez:', hiddenIndex);
      return;
    }
    
    // Beállítjuk a törlendő kép adatait (az adjusted indexet használjuk)
    setImageToDeleteIndex(adjustedIndex);
    setImageToDeleteUrl(imgToDelete.url);
    setIsDeleteModalOpen(true);
  };

  // Rejtett kép törlése - a tényleges törlési folyamat
  const handleRemoveHiddenImage = (index: number) => {
    console.log('Rejtett kép törlése elkezdődött:', index);
    console.log('Törlés előtti rejtett képek:', localHiddenImages.length, 'db');
    
    try {
      // Az eltávolítandó kép URL-jének azonosítása
      const imageToDeleteUrl = localHiddenImages[index]?.url;
      if (!imageToDeleteUrl) {
        console.error('Nincs URL a törölni kívánt rejtett képhez:', index);
        return;
      }
      
      // Normalizáljuk az URL-t a cache busting paraméterek nélkül
      const normalizedDeleteUrl = normalizeUrl(imageToDeleteUrl);
      console.log('Törlendő rejtett kép URL:', truncateUrl(normalizedDeleteUrl, 30));
      
      // Adjuk hozzá a törölt URL-t a feketelistához
      lastUploadedImages.deletedHiddenImageUrls.add(normalizedDeleteUrl);
      lastUploadedImages.hasChanges = true;
      lastUploadedImages.lastModified = Date.now();
      
      // URL alapú eltávolítás - ez biztonságosabb mint index alapú
      const remainingHiddenImages = localHiddenImages.filter(img => !isSameImage(img.url, imageToDeleteUrl));
      
      // Ellenőrizzük, hogy tényleg eltávolítottuk-e a képet
      if (remainingHiddenImages.length >= localHiddenImages.length) {
        console.error('A rejtett kép nem lett eltávolítva! Várt hossz:', localHiddenImages.length - 1, 'aktuális:', remainingHiddenImages.length);
        // Próbáljuk index alapú törléssel
        const indexBasedFiltered = [...localHiddenImages];
        indexBasedFiltered.splice(index, 1);
        remainingHiddenImages.length = 0; // Töröljük a tartalmat
        indexBasedFiltered.forEach(img => remainingHiddenImages.push({...img})); // Új objektumok hozzáadása
      }
      
      console.log('Törlés után maradó rejtett képek:', remainingHiddenImages.length, 'db');

      // Frissítések
      setValue('hiddenImages', JSON.parse(JSON.stringify(remainingHiddenImages))); // Mély másolat
      lastUploadedImages.hiddenImages = remainingHiddenImages.map(img => ({...img})); // Mély másolat 

      // Azonnal megerősítés
      setTimeout(() => {
        const formHiddenImages = watch('hiddenImages') || [];
        console.log('Azonnali ellenőrzés - rejtett képek száma form:', formHiddenImages.length);
        
        // Csak a setLocalHiddenImages hívás után módosítjuk a DOM-ot
        setLocalHiddenImages(remainingHiddenImages.map(img => ({...img}))); // Újabb mély másolat
      }, 0);
      
      // Felhasználói visszajelzés
      console.log('Rejtett kép törlés befejeződött:', index);
      // Az üzenetet a handleRemoveImage fogja megjeleníteni
    } catch (error) {
      console.error('Hiba a rejtett kép törlése közben:', error);
      notify('Hiba történt a rejtett kép törlése közben.', 'error');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? t('products.form.edit') : t('products.form.add')}
      size="5xl"
      noPadding
    >
      {/* Kép törlési megerősítő dialógus */}
      <ConfirmImageDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleRemoveImage}
        imageUrl={imageToDeleteUrl}
      />
      
      <form noValidate ref={formRef} onSubmit={handleSubmit(handleFormSubmit, onInvalid)} className="flex flex-col min-h-full">
        <div className="px-4 sm:px-6 flex-1 pb-5 space-y-4">
        <div className="sticky top-0 z-10 bg-[var(--crm-surface)] pt-3 sm:pt-4 !mt-0">
        <FormTabs
          sticky={false}
          tabs={[
            { id: 'basic', label: 'Alapadatok' },
            { id: 'pricing', label: 'Árazás' },
            { id: 'images', label: 'Képek' },
            ...(watch('type') === 'package' ? [{ id: 'package', label: 'Csomag tartalma' }] : []),
          ]}
          active={activeTab}
          onChange={(id) => setActiveTab(id as FormTab)}
          errorTabs={tabErrors}
        />
        </div>

        <div>
        <div data-tab="basic" className={tabCls('basic')}>
        {/* Type Selection */}
        <div className="crm-card overflow-hidden">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CubeIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('products.form.type.title')}</h3>
          </div>
          <div className="p-4">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setValue('type', 'product')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${productType === 'product'
                    ? 'bg-blue-500/20 dark:bg-blue-500/30 border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-400 font-medium shadow-sm'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    }`}
                >
                  <div className={`p-1.5 rounded-full ${productType === 'product' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <CubeIcon className={`w-4 h-4 ${productType === 'product' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  </div>
                  <span className="text-xs">{t('products.form.type.product')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setValue('type', 'service')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${productType === 'service'
                    ? 'bg-primary-500/20 dark:bg-primary-500/30 border-primary-500 dark:border-primary-400 text-primary-700 dark:text-primary-400 font-medium shadow-sm'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                    }`}
                >
                  <div className={`p-1.5 rounded-full ${productType === 'service' ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <WrenchScrewdriverIcon className={`w-4 h-4 ${productType === 'service' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  </div>
                  <span className="text-xs">{t('products.form.type.service')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setValue('type', 'package')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${productType === 'package'
                    ? 'bg-green-500/20 dark:bg-green-500/30 border-green-500 dark:border-green-400 text-green-700 dark:text-green-400 font-medium shadow-sm'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-green-300 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                >
                  <div className={`p-1.5 rounded-full ${productType === 'package' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <TagIcon className={`w-4 h-4 ${productType === 'package' ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  </div>
                  <span className="text-xs">Szolgáltatási csomag</span>
                </button>
              </div>
              {/* isService rejtett mező */}
              <input
                type="hidden"
                {...register("isService")}
              />
            </div>
          </div>
        </div>

        {/* Organization & Status Selection */}
        <div className="crm-card overflow-hidden">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szervezet és státusz</h3>
          </div>
          <div className="p-4">
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setValue('organizationId', '')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                    !watch('organizationId')
                      ? 'bg-gray-500/20 dark:bg-gray-500/30 border-gray-500 dark:border-gray-400 text-gray-700 dark:text-gray-400 font-medium shadow-sm'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-900/20'
                  }`}
                >
                  <div className={`p-1.5 rounded-full ${!watch('organizationId') ? 'bg-gray-100 dark:bg-gray-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <svg className={`w-4 h-4 ${!watch('organizationId') ? 'text-gray-600 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-xs">Globális</span>
                </button>
                {organizations.map(org => (
                  <button
                    key={org.id}
                    type="button"
                    onClick={() => setValue('organizationId', org.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                      watch('organizationId') === org.id
                        ? 'bg-primary-500/20 dark:bg-primary-500/30 border-primary-500 dark:border-primary-400 text-primary-700 dark:text-primary-400 font-medium shadow-sm'
                        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                    }`}
                  >
                    <div className={`p-1.5 rounded-full ${watch('organizationId') === org.id ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <svg className={`w-4 h-4 ${watch('organizationId') === org.id ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <span className="text-xs">{org.name}</span>
                  </button>
                ))}
                
                {/* Aktív státusz gomb */}
                <button
                  type="button"
                  onClick={() => setValue('isActive', !watch('isActive'))}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                    watch('isActive')
                      ? 'bg-green-500/20 dark:bg-green-500/30 border-green-500 dark:border-green-400 text-green-700 dark:text-green-400 font-medium shadow-sm'
                      : 'bg-red-500/20 dark:bg-red-500/30 border-red-500 dark:border-red-400 text-red-700 dark:text-red-400 font-medium shadow-sm'
                  }`}
                >
                  <div className={`p-1.5 rounded-full ${watch('isActive') ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    {watch('isActive') ? (
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <span className="text-xs">{watch('isActive') ? 'Aktív' : 'Inaktív'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-gray-400 mr-2">
              <FORM_SECTIONS.details.icon className="h-4 w-4" />
            </div>
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('products.sections.details')}</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.name')}</label>
                <div className="relative">
                  <input
                    type="text"
                    {...register("name", { required: true })}
                    className={`block w-full bg-white dark:bg-gray-700 border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200`}
                    placeholder={t('products.fields.name')}
                  />
                  {errors.name && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.name && <p className="mt-1 text-xs text-red-500">{t('products.validation.nameRequired')}</p>}
              </div>
              
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('products.fields.code')}
                  {!initialData && (
                    <span className="ml-1 text-xs text-blue-600 dark:text-blue-400 font-normal">{t('products.codeGeneration.autoGenerated')}</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register("code", { required: true })}
                    className={`block w-full bg-white dark:bg-gray-700 border ${
                      errors.code 
                        ? 'border-red-500' 
                        : !codeValidation.isValid 
                        ? 'border-red-500' 
                        : codeValidation.message && codeValidation.isValid 
                        ? 'border-green-500' 
                        : 'border-gray-300 dark:border-gray-600'
                    } rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200`}
                    placeholder={!initialData ? t('products.codeGeneration.placeholder') : t('products.fields.code')}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {codeValidation.isChecking && (
                      <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>
                    )}
                    {!codeValidation.isChecking && errors.code && (
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                    {!codeValidation.isChecking && !errors.code && !codeValidation.isValid && (
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                    {!codeValidation.isChecking && !errors.code && codeValidation.isValid && codeValidation.message && (
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
                {!initialData && !codeValidation.message && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('products.codeGeneration.helpText')}
                  </p>
                )}
                {codeValidation.message && (
                  <p className={`mt-1 text-xs ${codeValidation.isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {codeValidation.message}
                  </p>
                )}
                {errors.code && !codeValidation.message && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">{t('products.validation.codeRequired')}</p>
                )}
              </div>
              
              {/* Cikkszám mező */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.articleNumber') || 'Cikkszám'}</label>
                <div className="relative">
                  <input
                    type="text"
                    {...register("articleNumber")}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder={t('products.fields.articleNumber') || 'Cikkszám'}
                  />
                </div>
              </div>
              
              <div className="col-span-12 sm:col-span-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.unit')}</label>
                <select
                  {...register("unit")}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                >
                  <option value="db">{t('products.units.piece')}</option>
                  <option value="kg">{t('products.units.kg')}</option>
                  <option value="liter">{t('products.units.liter')}</option>
                  <option value="méter">{t('products.units.meter')}</option>
                  <option value="csomag">{t('products.units.package')}</option>
                  <option value="karton">{t('products.units.box')}</option>
                  <option value="óra">{t('products.units.hour')}</option>
                  <option value="nap">{t('products.units.day')}</option>
                  <option value="hónap">{t('products.units.month')}</option>
                </select>
              </div>
              
              <div className="col-span-12">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.description')}</label>
                <textarea
                  rows={3}
                  {...register("description")}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder={t('products.fields.description')}
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-gray-400 mr-2">
              <FORM_SECTIONS.categories.icon className="h-4 w-4" />
            </div>
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('products.sections.categories')}</h3>
          </div>
          
          <div className="space-y-4">
            {/* Kategória kereső és kiválasztó */}
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                {/* Main categories */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                  <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('products.fields.category1')}</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {mainCategories.map(category => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => {
                          const categoryId = category.id;
                          setValue('category', {
                            categoryId: categoryId,
                            level1: category.name,
                            fullPath: category.name
                          });
                          // Clear subcategory when main category changes
                          setValue('category.level2', undefined);
                        }}
                        className={`flex items-center space-x-1.5 px-2 py-1.5 rounded-lg border text-xs ${
                          watchedCategory.categoryId === category.id
                            ? 'bg-orange-500/20 dark:bg-orange-500/30 border-orange-500 dark:border-orange-400 text-orange-700 dark:text-orange-400 font-medium'
                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-orange-300 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                        }`}
                      >
                        <div className="p-1 rounded bg-gradient-to-br from-orange-500/20 to-yellow-600/20 border-orange-500/20 border">
                          <TagIcon className="h-3 w-3 text-orange-600" />
                        </div>
                        <span>{category.name}</span>
                      </button>
                    ))}

                    {mainCategories.length === 0 && (
                      <div className="w-full px-3 py-2 text-xs text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                        {t('products.noCategories')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Subcategories - only show if a main category is selected */}
                {watchedCategory.categoryId && (
                  <div className="p-3">
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('products.fields.category2')}</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {/* Subcategories for the selected main category */}
                      {availableSubcategories.length > 0 ? (
                        availableSubcategories.map(subCategory => (
                          <button
                            key={subCategory.id}
                            type="button"
                            onClick={() => {
                              const subCategoryId = subCategory.id;
                              const selectedMainCat = categories.find(cat => cat.id === watchedCategory.categoryId);
                              
                              if (selectedMainCat) {
                                setValue('category', {
                                  ...watchedCategory,
                                  subCategoryId: subCategoryId,
                                  level2: subCategory.name,
                                  fullPath: `${selectedMainCat.name} / ${subCategory.name}`
                                });
                              }
                            }}
                            className={`flex items-center space-x-1.5 px-2 py-1.5 rounded-lg border text-xs ${
                              watchedCategory.subCategoryId === subCategory.id
                                ? 'bg-amber-500/20 dark:bg-amber-500/30 border-amber-500 dark:border-amber-400 text-amber-700 dark:text-amber-400 font-medium'
                                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-amber-300 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                            }`}
                          >
                            <div className="p-1 rounded bg-gradient-to-br from-amber-500/20 to-yellow-600/20 border-amber-500/20 border">
                              <TagIcon className="h-3 w-3 text-amber-600" />
                            </div>
                            <span>{subCategory.name}</span>
                          </button>
                        ))
                      ) : (
                        <div className="w-full px-3 py-2 text-xs text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          {t('products.noSubcategories')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Kiválasztott kategória megjelenítése */}
            {(watchedCategory.level1 || watchedCategory.level2) && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('products.selectedCategory')}</h4>
                <div className="flex items-center space-x-1 text-sm">
                  {watchedCategory.level1 && (
                    <div className="flex items-center">
                      <div className="p-1 rounded bg-gradient-to-br from-orange-500/20 to-yellow-600/20 border-orange-500/20 border mr-1.5">
                        <TagIcon className="h-3 w-3 text-orange-600" />
                      </div>
                      <span className="text-gray-800 dark:text-gray-200">{watchedCategory.level1}</span>
                    </div>
                  )}
                  {watchedCategory.level2 && (
                    <>
                      <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                      <div className="flex items-center">
                        <div className="p-1 rounded bg-gradient-to-br from-amber-500/20 to-yellow-600/20 border-amber-500/20 border mr-1.5">
                          <TagIcon className="h-3 w-3 text-amber-600" />
                        </div>
                        <span className="text-gray-800 dark:text-gray-200">{watchedCategory.level2}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        </div>

        <div data-tab="pricing" className={tabCls('pricing')}>
        {/* Additional Pricing Options */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-gray-400 mr-2">
              <FORM_SECTIONS.pricing.icon className="h-4 w-4" />
            </div>
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('products.sections.additionalPricing')}</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Valuta</label>
                <select
                  {...register("currency")}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                >
                  <option value="HUF">HUF</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              
              <div className="col-span-12 sm:col-span-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.netPrice')}</label>
                <input
                  type="number"
                  step="0.01"
                  {...register("netPrice")}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder={t('products.fields.netPrice')}
                />
              </div>
              
              <div className="col-span-12 sm:col-span-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.taxRate')}</label>
                <select
                  {...register("taxRate")}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                >
                  <option value="">{t('products.form.selectTaxRate')}</option>
                  <option value="27%">27%</option>
                  <option value="18%">18%</option>
                  <option value="5%">5%</option>
                  <option value="0%">0%</option>
                  <option value="AAM">AAM</option>
                  <option value="TAM">TAM</option>
                  <option value="EU">EU</option>
                  <option value="EUK">EUK</option>
                  <option value="ÁKK">ÁKK</option>
                  <option value="HÉA">HÉA</option>
                  <option value="ÁTHK">ÁTHK</option>
                  <option value="TM">TM</option>
                </select>
              </div>
              
              <div className="col-span-12 sm:col-span-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.rounding')}</label>
                <input
                  type="text"
                  {...register("rounding")}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="0"
                />
              </div>
              
              <div className="col-span-12 sm:col-span-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.webStore')}</label>
                    <div className="mt-1.5">
                      <label className="inline-flex items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-500">
                        <input
                          type="checkbox"
                          {...register("webStore")}
                          className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 bg-white dark:bg-gray-800"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('common.yes')}</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.discontinued')}</label>
                    <div className="mt-1.5">
                      <label className="inline-flex items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-500">
                        <input
                          type="checkbox"
                          {...register("discontinued")}
                          className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 bg-white dark:bg-gray-800"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('common.yes')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-span-12 sm:col-span-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{t('products.fields.reverseTaxation')}</label>
                <div className="mt-1.5">
                  <label className="inline-flex items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-500">
                    <input
                      type="checkbox"
                      {...register("reverseTaxation")}
                      className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 bg-white dark:bg-gray-800"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('common.yes')}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>

        <div data-tab="images" className={tabCls('images')}>
        {/* Kép feltöltő rész */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-gray-400 mr-2">
              <FORM_SECTIONS.images.icon className="h-4 w-4" />
            </div>
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('products.sections.images')}</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            {/* Összefoglaló rész egyszerűsítése */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Képek összefoglaló</h4>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {localImages.length > 0 
                  ? `${localImages.length} kép van feltöltve` 
                  : 'Nincs kép feltöltve'}
              </div>
            </div>
            
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12">
                {/* Thumbnail szekció */}
                <div className="border-b border-gray-200 dark:border-gray-600 pb-5 mb-5">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('products.fields.thumbnailImage')}
                </label>
                
                <div className="flex items-start space-x-4">
                    {/* Thumbnail előnézet */}
                  <div className="w-32 h-32 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                    {thumbnailUrl ? (
                        <div className="relative w-full h-full">
                      <img 
                            key={`thumb-${thumbnailUrl?.split('?')[0]}`}
                        src={thumbnailUrl} 
                            alt="Termékkép (thumbnail)"
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'contain', 
                              padding: '4px',
                              backgroundColor: 'white'
                            }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-primary-500 text-white text-[10px] px-1 py-0.5 text-center">
                            Thumbnail
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center">
                          <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-500 mb-2" />
                          <p className="text-xs text-gray-500 dark:text-gray-400">Nincs thumbnail</p>
                        </div>
                    )}
                  </div>
                  
                    {/* Feltöltés információk */}
                  <div className="flex-1">
                    <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Thumbnail / fő kép beállítása</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          A fő képként jelölt kép lesz a termék thumbnail képe is. Használja a lenti képgalériában a ⭐ gombot a fő kép kiválasztásához.
                        </p>
                      </div>
                      
                      <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                        <p className="font-medium mb-1">Tippek a képfeltöltéshez:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Használjon világos hátteret</li>
                          <li>A termék legyen a kép középpontjában</li>
                          <li>Ajánlott képméret: legalább 800x800 pixel</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* További képek kezelése */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {t('products.sections.productImages')} ({localImages.length} db)
                    </h4>
                    
                    {/* Kép feltöltés gomb - egységesítve */}
                    <div className="flex items-center">
                      <input
                        id="product-image-upload"
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                        className="hidden"
                        multiple
                      />
                      <label
                        htmlFor="product-image-upload"
                        className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        {isUploading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Feltöltés folyamatban...
                          </>
                        ) : (
                          <>
                            <PlusIcon className="h-3 w-3 mr-1" />
                            Képek feltöltése
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  {/* Képgaléria */}
                  {localImages.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {localImages.map((img, index) => (
                        <div 
                          key={`img-${index}-${img.url?.split('?')[0]}`} 
                          className={`aspect-square rounded-md border overflow-hidden relative group ${
                            img.isMain ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-700' : 'border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          {img.url ? (
                            <>
                              <img 
                                src={img.url}
                                alt={`Termék kép ${index+1}`}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'contain', 
                                  padding: '8px',
                                  backgroundColor: 'white'
                                }}
                              />
                              
                              {/* Főkép jelölő */}
                              {img.isMain && (
                                <div className="absolute top-0 left-0 bg-primary-500 text-white text-[10px] px-1 py-0.5">
                                  Fő kép
                    </div>
                              )}
                              
                              {/* Kép sorszáma */}
                              <div className="absolute top-0 right-0 bg-gray-700 dark:bg-gray-900 text-white text-[10px] px-1 py-0.5">
                                #{index+1}
                  </div>
                              
                              {/* Kép műveletek - mindig láthatóak, és jobb alsó sarokban vannak */}
                              <div className="absolute bottom-1 right-1 flex space-x-1">
                                {/* Fő kép beállítása */}
                                {!img.isMain && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSetMainImage(index);
                                      return false;
                                    }}
                                    className="p-1.5 rounded-full bg-white dark:bg-gray-800 bg-opacity-80 shadow-sm text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                    title="Beállítás fő képként"
                                  >
                                    <StarIcon className="h-4 w-4" />
                                  </button>
                                )}
                                
                                {/* Kép törlése */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Törlés gomb megnyomva:', index);
                                    handleImageDeleteRequest(index);
                                  }}
                                  className="p-1.5 rounded-full bg-white dark:bg-gray-800 bg-opacity-80 shadow-sm text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                  title="Kép törlése"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                </div>
                            </>
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <PhotoIcon className="w-6 h-6 text-gray-300 dark:text-gray-500" />
              </div>
                          )}
            </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Még nincsenek feltöltött képek. Kattintson a "További kép" gombra a képek feltöltéséhez.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hidden Images */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-gray-400 mr-2">
              <FORM_SECTIONS.hiddenImages.icon className="h-4 w-4" />
            </div>
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('products.sections.hiddenImages')}</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            {/* Összefoglaló rész egyszerűsítése */}
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">⚠️ Rejtett képek</h4>
              <div className="text-xs text-red-600 dark:text-red-400">
                {localHiddenImages?.length > 0 
                  ? `${localHiddenImages.length} rejtett kép van feltöltve - ezeket csak adminisztrátorok látják` 
                  : 'Nincsenek rejtett képek feltöltve'}
              </div>
            </div>
            
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12">
                {/* Rejtett képek kezelése */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Rejtett képek ({localHiddenImages?.length || 0} db)
                    </h4>
                    
                    {/* Rejtett kép feltöltés gomb */}
                    <div className="flex items-center">
                      <input
                        id="hidden-image-upload"
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleHiddenImageUpload}
                        disabled={isUploadingHidden}
                        className="hidden"
                        multiple
                      />
                      <label
                        htmlFor="hidden-image-upload"
                        className="inline-flex items-center justify-center px-3 py-1.5 border border-red-300 dark:border-red-700 rounded-md shadow-sm text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 cursor-pointer"
                      >
                        {isUploadingHidden ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Feltöltés folyamatban...
                          </>
                        ) : (
                          <>
                            <PlusIcon className="h-3 w-3 mr-1" />
                            Rejtett képek feltöltése
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  {/* Rejtett képgaléria */}
                  {localHiddenImages && localHiddenImages.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {localHiddenImages.map((img, index) => (
                        <div 
                          key={`hidden-img-${index}-${img.url?.split('?')[0]}`} 
                          className="aspect-square rounded-md border border-red-200 dark:border-red-700 overflow-hidden relative group bg-red-50 dark:bg-red-900/20"
                        >
                          {img.url ? (
                            <>
                              <img 
                                src={img.url}
                                alt={`Rejtett kép ${index+1}`}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'contain', 
                                  padding: '8px',
                                  backgroundColor: 'white'
                                }}
                              />
                              
                              {/* Rejtett jelölő */}
                              <div className="absolute top-0 left-0 bg-red-500 text-white text-[10px] px-1 py-0.5">
                                Rejtett
                              </div>
                              
                              {/* Kép sorszáma */}
                              <div className="absolute top-0 right-0 bg-gray-700 text-white text-[10px] px-1 py-0.5">
                                #{index+1}
                              </div>
                              
                              {/* Kép törlése */}
                              <div className="absolute bottom-1 right-1">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // A rejtett képek indexét a normál képek száma után kezdjük
                                    const adjustedIndex = localImages.length + index;
                                    handleHiddenImageDeleteRequest(adjustedIndex);
                                  }}
                                  className="p-1.5 rounded-full bg-white dark:bg-gray-800 bg-opacity-80 shadow-sm text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                  title="Rejtett kép törlése"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </>
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <PhotoIcon className="w-6 h-6 text-red-300 dark:text-red-500" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-red-300 dark:border-red-700 rounded-lg p-4 bg-red-50 dark:bg-red-900/20 text-center">
                      <p className="text-sm text-red-500 dark:text-red-400">Még nincsenek feltöltött rejtett képek. Ezeket csak az adminisztrátorok láthatják majd.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>

        <div data-tab="package" className={tabCls('package')}>
        {/* Package Services Section - csak package típusnál */}
        {productType === 'package' && (
          <div className="crm-card overflow-hidden">
            <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <WrenchScrewdriverIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szolgáltatási csomag részletei</h3>
          </div>
          <div className="p-4">
                
                {/* Termék/Szolgáltatás tallózó */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Csomag tartalma
                    </label>
                    
                    {/* Termék hozzáadása */}
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3">
                        Termék/Szolgáltatás hozzáadása
                      </h4>
                      <div className="relative" ref={productDropdownRef}>
                        <div className="relative">
                          <input
                            type="text"
                            value={productSearchQuery}
                            onChange={(e) => {
                              setProductSearchQuery(e.target.value);
                              setIsProductDropdownOpen(true);
                            }}
                            onFocus={() => setIsProductDropdownOpen(true)}
                            placeholder="Keress terméket vagy szolgáltatást..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                          />
                          <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                        </div>
                        
                        {/* Kereshető dropdown lista */}
                        {isProductDropdownOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {products
                              .filter(p => p.isActive && p.type !== 'package')
                              .filter(p => 
                                productSearchQuery === '' ||
                                p.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                                p.code.toLowerCase().includes(productSearchQuery.toLowerCase())
                              )
                              .map(product => (
                                <button
                                  key={product.id}
                                  type="button"
                                  onClick={() => {
                                    setPackageServices([...packageServices, {
                                      productId: product.id,
                                      productCode: product.code,
                                      name: product.name,
                                      description: product.description,
                                      quantity: 1,
                                      unit: product.unit || 'db',
                                      hours: product.type === 'service' ? 1 : undefined,
                                      isIncluded: true
                                    }]);
                                    setProductSearchQuery('');
                                    setIsProductDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {product.name}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {product.code} • {product.type === 'service' ? 'Szolgáltatás' : 'Termék'}
                                      </div>
                                    </div>
                                    <PlusIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                                  </div>
                                </button>
                              ))}
                            {products
                              .filter(p => p.isActive && p.type !== 'package')
                              .filter(p => 
                                productSearchQuery === '' ||
                                p.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                                p.code.toLowerCase().includes(productSearchQuery.toLowerCase())
                              ).length === 0 && (
                              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nincs találat
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Hozzáadott termékek listája */}
                    {packageServices.map((service, index) => (
                      <div key={index} className="mb-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {service.name}
                            </h4>
                            {service.productCode && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({service.productCode})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={service.isIncluded !== false}
                                onChange={(e) => {
                                  const newServices = [...packageServices];
                                  newServices[index].isIncluded = e.target.checked;
                                  setPackageServices(newServices);
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-xs text-gray-600 dark:text-gray-400">{t('products.form.package.includedInPrice')}</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const newServices = packageServices.filter((_, i) => i !== index);
                                setPackageServices(newServices);
                              }}
                              className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Mennyiség</label>
                            <input
                              type="number"
                              value={service.quantity || 1}
                              onChange={(e) => {
                                const newServices = [...packageServices];
                                newServices[index].quantity = parseInt(e.target.value) || 1;
                                setPackageServices(newServices);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Egység</label>
                            <input
                              type="text"
                              value={service.unit || 'db'}
                              onChange={(e) => {
                                const newServices = [...packageServices];
                                newServices[index].unit = e.target.value;
                                setPackageServices(newServices);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                            />
                          </div>
                          {service.hours !== undefined && (
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Órák</label>
                              <input
                                type="number"
                                value={service.hours || 0}
                                onChange={(e) => {
                                  const newServices = [...packageServices];
                                  newServices[index].hours = parseInt(e.target.value) || 0;
                                  setPackageServices(newServices);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-100"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {packageServices.length === 0 && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <WrenchScrewdriverIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Még nincs termék/szolgáltatás hozzáadva a csomaghoz</p>
                        <p className="text-xs mt-1">Válassz a fenti legördülő menüből</p>
                      </div>
                    )}
                  </div>

                  {/* Csomag részletek */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Becsült időtartam
                      </label>
                      <input
                        type="text"
                        placeholder="pl. 6-8 hét"
                        value={packageDetails.estimatedDuration || ''}
                        onChange={(e) => setPackageDetails(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Összes óra
                      </label>
                      <input
                        type="number"
                        value={packageDetails.totalHours || ''}
                        onChange={(e) => setPackageDetails(prev => ({ ...prev, totalHours: parseInt(e.target.value) || undefined }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Módszertan
                    </label>
                    <textarea
                      placeholder="Leírás a munkamódszerről..."
                      value={packageDetails.methodology || ''}
                      onChange={(e) => setPackageDetails(prev => ({ ...prev, methodology: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                    />
                  </div>

                  {/* Deliverables */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Szállítandók
                    </label>
                    <div className="space-y-2">
                      {(packageDetails.deliverables || []).map((deliverable, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={deliverable}
                            onChange={(e) => {
                              const newDeliverables = [...(packageDetails.deliverables || [])];
                              newDeliverables[index] = e.target.value;
                              setPackageDetails(prev => ({ ...prev, deliverables: newDeliverables }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Szállítandó elem"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newDeliverables = (packageDetails.deliverables || []).filter((_, i) => i !== index);
                              setPackageDetails(prev => ({ ...prev, deliverables: newDeliverables }));
                            }}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newDeliverables = [...(packageDetails.deliverables || []), ''];
                          setPackageDetails(prev => ({ ...prev, deliverables: newDeliverables }));
                        }}
                        className="flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>Szállítandó hozzáadása</span>
                      </button>
                    </div>
                  </div>

                  {/* Requirements */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Követelmények
                    </label>
                    <div className="space-y-2">
                      {(packageDetails.requirements || []).map((requirement, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={requirement}
                            onChange={(e) => {
                              const newRequirements = [...(packageDetails.requirements || [])];
                              newRequirements[index] = e.target.value;
                              setPackageDetails(prev => ({ ...prev, requirements: newRequirements }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Követelmény"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newRequirements = (packageDetails.requirements || []).filter((_, i) => i !== index);
                              setPackageDetails(prev => ({ ...prev, requirements: newRequirements }));
                            }}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newRequirements = [...(packageDetails.requirements || []), ''];
                          setPackageDetails(prev => ({ ...prev, requirements: newRequirements }));
                        }}
                        className="flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>Követelmény hozzáadása</span>
                      </button>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ütemterv
                    </label>
                    <div className="space-y-3">
                      {(packageDetails.timeline || []).map((phase, index) => (
                        <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              Fázis #{index + 1}
                            </h5>
                            <button
                              type="button"
                              onClick={() => {
                                const newTimeline = (packageDetails.timeline || []).filter((_, i) => i !== index);
                                setPackageDetails(prev => ({ ...prev, timeline: newTimeline }));
                              }}
                              className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                            <input
                              type="text"
                              placeholder="Fázis neve"
                              value={phase.phase}
                              onChange={(e) => {
                                const newTimeline = [...(packageDetails.timeline || [])];
                                newTimeline[index].phase = e.target.value;
                                setPackageDetails(prev => ({ ...prev, timeline: newTimeline }));
                              }}
                              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                            />
                            <input
                              type="text"
                              placeholder="Időtartam (pl. 2 hét)"
                              value={phase.duration}
                              onChange={(e) => {
                                const newTimeline = [...(packageDetails.timeline || [])];
                                newTimeline[index].duration = e.target.value;
                                setPackageDetails(prev => ({ ...prev, timeline: newTimeline }));
                              }}
                              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                            />
                          </div>
                          <textarea
                            placeholder="Fázis leírása"
                            value={phase.description}
                            onChange={(e) => {
                              const newTimeline = [...(packageDetails.timeline || [])];
                              newTimeline[index].description = e.target.value;
                              setPackageDetails(prev => ({ ...prev, timeline: newTimeline }));
                            }}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newTimeline = [...(packageDetails.timeline || []), { phase: '', duration: '', description: '' }];
                          setPackageDetails(prev => ({ ...prev, timeline: newTimeline }));
                        }}
                        className="flex items-center space-x-2 px-3 py-2 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>Fázis hozzáadása</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        </div>
        </div>

        </div>
        {/* Mentés sáv – rögzítve alul */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-4 sm:px-6 py-3 border-t border-[var(--crm-border)] bg-[var(--crm-surface)]">
          <button type="button" onClick={onCancel} className="crm-btn crm-btn-secondary">
            {t('common.cancel')}
          </button>
          <button type="submit" className="crm-btn crm-btn-primary">
            {initialData ? t('products.form.buttons.update') : t('products.form.buttons.add')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductForm; 